import {
  GymSchema,
  MemberScheduleSchema,
  OccupancyDigestSchema,
  BroadcastMessageSchema,
  BroadcastManifestSchema,
  BroadcastReceiptSchema,
  PushWakeGrantSchema,
  WebPushSubscriptionSchema,
  SyncRequestSchema,
  RoleCertificateSchema,
  buildOccupancyDigest,
  canonicalJson,
  commitGymDraft,
  occupancyFingerprint,
  channelIdForOwner,
  opaqueChannelId,
  createChannelPass,
  createId,
  createIdentity,
  createInvite,
  createSignedOperation,
  createRoleRevocation,
  createRecipientStatus,
  createPushWake,
  createPushWakeGrant,
  createPushWakeRevocation,
  recordInviteConsumption,
  verifyInviteConsumption,
  InviteConsumptionSchema,
  recoveryChannelId,
  verifyChannelAdmission,
  decryptEnvelope,
  decryptProfileBackup,
  encryptEnvelope,
  encryptProfileBackup,
  identityFromMnemonic,
  identityIdFromSigningKey,
  issueRoleCertificate,
  applyDayBooking,
  cancelFutureBookings,
  resolveOperationAuthorization,
  cancelOccurrence,
  occurrencesForDate,
  deriveAlerts,
  mergeGym,
  mergeMemberSchedule,
  mergeOccupancyDigest,
  normalizeGym,
  normalizeMemberSchedule,
  normalizeOccupancyDigest,
  projectAttendance,
  pruneOccupancyDigest,
  sameDocument,
  selectRecoveryState,
  toPublicIdentity,
  formatInviteLinkUrl,
  sealInviteLink,
  verifyInvite,
  verifyInviteRejection,
  recordInviteRejection,
  verifyRoleCertificate,
  verifyRoleRevocation,
  verifyPushWakeGrant,
  verifySignedOperation,
  InviteRejectionSchema,
  ProfileBackupSchema,
  RoleRevocationSchema,
  type Gym,
  type IssuedInvite,
  type AuthorizedPublicIdentity,
  type InviteConsumption,
  type RoleRevocation,
  type CapacityAlert,
  type BroadcastMessage,
  type BroadcastManifest,
  type BroadcastReceipt,
  type PushWake,
  type PushWakeGrant,
  type ChannelProof,
  type IdentityMaterial,
  type MemberSchedule,
  type OccupancyDigest,
  type ProfileBackup,
  type PublicIdentity,
  type Role,
  type RoleCertificate,
  type SignedInvite,
  type SignedOperation,
  type SyncEnvelope,
} from "@palestra/core";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";
import { decodeInvite } from "@palestra/core";
import { RejectedEnvelopeError, RelaySyncClient, type SyncSnapshot } from "./sync-client";
import {
  alignmentInventory,
  documentFingerprint,
  needsChannelPass,
  occupancyInventoryFingerprint,
} from "./alignment";
import { readRuntimeConfig, resolveRelayUrl } from "./runtime-config";
import { cleanPathKeepingSim, reportSimSyncState, simStorageSuffix } from "./dev-session";
import {
  confirmIdentityBackup,
  closeLocalDatabase,
  deleteLocalProfileData,
  ensureDeviceId,
  enqueue,
  getMeta,
  loadDocument,
  loadCoachProjectionDocument,
  loadGymDocuments,
  loadIdentity,
  loadMemberScheduleDocument,
  loadOccupancyDocument,
  loadBroadcastState,
  markBroadcastEnvelopePublished,
  removeMemberScheduleDocument,
  saveCoachProjectionDocument,
  saveBroadcastDocument,
  saveBroadcastManifestDocument,
  saveBroadcastReceiptDocument,
  saveBroadcastOutboxBundle,
  saveBroadcastReceiptOutbox,
  saveDocument,
  saveGymDocuments,
  saveIdentity,
  saveMemberScheduleDocument,
  saveOccupancyDocument,
  setMeta,
  type StoredBroadcastDocument,
  type BroadcastReceiptProjection,
} from "./storage";

export interface LocalProfile {
  displayName: string;
  role: Role;
  gymId: string;
  /**
   * Le revoche che questo dispositivo ricorda, e gli inviti gia' spesi o
   * ritirati. Facoltativi perche' i profili nati prima non li hanno; viaggiano
   * nel backup insieme al resto, altrimenti un ripristino disferebbe una revoca.
   */
  revokedPeers?: RoleRevocation[] | undefined;
  spentInvites?: InviteConsumption[] | undefined;
  /**
   * Presente solo quando la revoca ricevuta riguarda questa identita': e' cio'
   * che permette all'app di dire «sei fuori» invece di fingere che le
   * prenotazioni partano. Si toglie accettando un invito nuovo.
   */
  revokedAccess?: { revokedAt: string; revokedBy: string } | undefined;
  /**
   * Presente solo su un dispositivo entrato con un invito che non valeva piu'
   * — gia' speso da un'altra persona, o ritirato. È la fine del silenzio: chi
   * lo riceve non era «in attesa del coach», non era entrato affatto.
   */
  rejectedAccess?:
    | {
        inviteId: string;
        reason: "spent" | "withdrawn";
        rejectedAt: string;
        rejectedBy: string;
      }
    | undefined;
  /**
   * La casella sul relay e il titolo per starci. Un profilo nato prima della
   * prova di appartenenza non li ha: resta perfettamente usabile in locale, ma
   * per tornare a sincronizzare gli serve un invito nuovo.
   */
  channelId?: string | undefined;
  channelProof?: ChannelProof | undefined;
  pushWakeGrant?: PushWakeGrant | undefined;
  peers: AuthorizedPeer[];
}

export type AuthorizedPeer = AuthorizedPublicIdentity;

/**
 * Un invito emesso, per poterlo mostrare e ritirare. Vive in locale e nel
 * backup cifrato del profilo — non piu' in un solo dispositivo: un invito
 * creato dal telefono si ritira anche dal computer. Nessun altro ha motivo di
 * sapere quali inviti hai in giro finche' non vengono usati.
 */
export type { IssuedInvite };

interface StoreState {
  ready: boolean;
  identity?: IdentityMaterial;
  backupConfirmed: boolean;
  profile?: LocalProfile;
  gym?: Gym;
  schedules: MemberSchedule[];
  /** Conteggi per occorrenza: l'unico modo in cui un atleta sa quanti altri ci sono. */
  occupancy?: OccupancyDigest;
  alerts: CapacityAlert[];
  broadcasts: StoredBroadcastDocument[];
  broadcastManifests: BroadcastManifest[];
  broadcastReceipts: BroadcastReceiptProjection[];
  pushPublicKey?: string;
  notificationPermission: NotificationPermission | "unsupported";
  pushSubscribed: boolean;
  /** Gli inviti emessi da questo dispositivo e non ancora spesi né scaduti. */
  invites: IssuedInvite[];
  /**
   * L'invito con cui questo dispositivo è entrato. Serve a riconoscere il
   * proprio link quando lo si riapre — succede, e senza questo confronto
   * l'ingresso ripartiva da capo cancellando la pianificazione di chi era già
   * dentro. Assente in chi ha fondato la palestra: non è entrato da nessuna
   * parte.
   */
  acceptedInviteId?: string;
  awaitingInitialSync: boolean;
  /**
   * L'ultima volta che un envelope dello staff e' stato applicato. E' la rete
   * di sicurezza per l'atleta la cui revoca non e' mai arrivata (dispositivo
   * spento oltre la retention della casella): un silenzio dello staff troppo
   * lungo va detto, perche' le prenotazioni potrebbero parlare al vuoto.
   */
  lastStaffContactAt?: string;
  sync: SyncSnapshot;
  error?: string;
}

interface StoreActions {
  createFounder: (displayName: string, gymName: string) => Promise<void>;
  acceptInvite: (displayName: string, invite: SignedInvite) => Promise<void>;
  restoreIdentity: (mnemonic: string) => Promise<void>;
  restoreProfile: (mnemonic: string) => Promise<RestoreOutcome>;
  confirmBackup: () => Promise<void>;
  updateGym: (gym: Gym, base?: Gym) => Promise<Gym>;
  updateSchedule: (schedule: MemberSchedule) => Promise<void>;
  book: (date: string, slotId: string) => Promise<void>;
  cancel: (date: string, sourceSlotId: string) => Promise<void>;
  removeMemberBooking: (memberId: string, date: string) => Promise<void>;
  removeMemberFutureBookings: (memberId: string) => Promise<void>;
  /**
   * Crea l'invito, ne deposita il blob cifrato sul relay e restituisce la voce
   * con l'URL corto pronto da condividere. Richiede il canale autenticato: un
   * link consegnato senza la cassetta dietro aprirebbe un 410 al primo tocco.
   */
  makeInvite: (role: "member" | "coach") => Promise<IssuedInvite>;
  /** Ritira un invito prima della scadenza: da qui in poi non apre piu' nulla. */
  revokeInvite: (inviteId: string) => Promise<void>;
  revokePeer: (peerId: string) => Promise<void>;
  dismissAlert: (alertId: string) => Promise<void>;
  sendBroadcast: (body: string) => Promise<string>;
  markBroadcastRead: (broadcastId: string) => Promise<void>;
  enableNotifications: () => Promise<void>;
  disableNotifications: () => Promise<void>;
  /**
   * `removeRemoteBackup` e' una decisione a parte, e va chiesta a parte: la
   * copia cifrata sul relay e' cio' che permette di rientrare con le sole 12
   * parole, quindi cancellarla non e' un dettaglio della rimozione locale.
   */
  removeProfile: (
    mnemonic: string,
    confirmation: string,
    removeRemoteBackup?: boolean,
  ) => Promise<void>;
  /**
   * Azzera il dispositivo senza chiedere nulla che il dispositivo debba essere
   * in grado di dare. `removeProfile` pretende le 12 parole dell'identita'
   * attiva e il backup gia' confermato: sono garanzie giuste per chi *lascia*
   * l'applicazione, ma diventano una porta chiusa proprio quando servirebbe
   * aprirla — un profilo a meta', una coda che non parte, un recupero da
   * riprovare da pulito. Qui basta la parola di conferma.
   *
   * Non tocca la copia cifrata sul relay: e' quella che permette di rientrare
   * con le sole 12 parole, ed e' esattamente cio' che si vuole conservare
   * quando si azzera per riprovare il recupero.
   */
  wipeDevice: (confirmation: string) => Promise<void>;
  clearError: () => void;
  reconnect: () => void;
}

/**
 * Lo stato di dominio non include `sync`: cambia con i dati — un envelope, una
 * prenotazione — mentre `sync` cambia a ogni tick della connessione. Tenerli
 * nello stesso valore di contesto faceva ri-renderizzare griglia e pannello
 * coach per ogni battito del badge (PERF-06).
 */
type DomainState = Omit<StoreState, "sync">;
type StoreValue = DomainState & StoreActions;
const DomainContext = createContext<DomainState | undefined>(undefined);
const ActionsContext = createContext<StoreActions | undefined>(undefined);
const SyncContext = createContext<SyncSnapshot | undefined>(undefined);
/**
 * Il canale segue il database (`dev-session.ts`): due sessioni simulate non
 * devono ricaricarsi né chiudersi il database a vicenda, mentre due schede
 * della *stessa* sessione — o della sessione di sempre, senza `?sim=` — restano
 * accoppiate come prima.
 */
const channel =
  typeof BroadcastChannel === "undefined"
    ? undefined
    : new BroadcastChannel(`palestra-local-state${simStorageSuffix()}`);

/**
 * Mai `synced` prima di aver parlato con il relay: la convergenza si dichiara
 * dopo un contatto, non perche' il browser crede di avere rete. Il client parte
 * dalla stessa premessa; questo e' il valore che vale finche' il client non c'e'.
 */
const emptySync: SyncSnapshot = { state: navigator.onLine ? "connecting" : "offline", pending: 0 };

function applicationServerKey(value: string): Uint8Array<ArrayBuffer> {
  const padded = `${value}${"=".repeat((4 - (value.length % 4)) % 4)}`
    .replace(/-/gu, "+")
    .replace(/_/gu, "/");
  const raw = atob(padded);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let index = 0; index < raw.length; index += 1) bytes[index] = raw.charCodeAt(index);
  return bytes;
}

async function unsubscribeBrowserPush(): Promise<void> {
  if (
    !("serviceWorker" in navigator) ||
    typeof navigator.serviceWorker.getRegistration !== "function"
  )
    return;
  const registration = await navigator.serviceWorker.getRegistration();
  await (await registration?.pushManager.getSubscription())?.unsubscribe();
}

/**
 * Una modifica arriva quasi sempre in raffica (sposta, conferma, rinomina): il
 * backup interessante e' quello che resta alla fine, non i passaggi intermedi.
 */
const BACKUP_DEBOUNCE_MS = 5_000;

/**
 * Tre modi di non avere il profilo, e vanno detti in modo diverso: il relay non
 * risponde, il relay dice che non c'e', il blob c'e' ma non si legge.
 */
export type RestoreOutcome =
  | { kind: "profile" }
  | { kind: "identity-only"; reason: "no-backup" | "relay-unreachable" | "unreadable" };

interface DomainMessage {
  operation: SignedOperation;
  author: PublicIdentity;
  authorization?: SignedInvite;
  /**
   * Il lasciapassare nominativo che lo staff consegna a chi ha gia' dimostrato
   * di essere dei nostri. Vale finche' non gli si toglie il ruolo, e sostituisce
   * quello al portatore dell'invito, che scade con l'invito stesso.
   */
  channelPass?: ChannelProof;
}

/**
 * Chi puo' leggere che cosa. La pianificazione di un atleta riguarda lo staff e
 * lui: mandarla a tutti i pari significherebbe cifrarla anche per gli altri
 * atleti, che potrebbero leggerla anche se l'app non la mostra. La capienza,
 * invece, e' fatta per essere vista da tutti — ed e' fatta di soli numeri.
 */
function recipientsFor(
  kind: SignedOperation["kind"],
  payload: unknown,
  self: PublicIdentity,
  peers: readonly AuthorizedPeer[],
): PublicIdentity[] {
  if (kind === "broadcast.manifest" || kind === "broadcast.receipt") {
    return [self, ...peers.filter((peer) => peer.role !== "member")];
  }
  if (kind === "push.wake.granted") {
    const grant = PushWakeGrantSchema.safeParse(payload);
    return [
      self,
      ...peers.filter(
        (peer) => peer.role !== "member" || (grant.success && peer.id === grant.data.subjectId),
      ),
    ];
  }
  if (kind === "occupancy.published") {
    // Ai coach non serve: hanno le pianificazioni vere.
    return [self, ...peers.filter((peer) => peer.role === "member")];
  }
  if (kind === "role.issued") {
    const certificate = RoleCertificateSchema.safeParse(payload);
    // Il certificato di un nuovo coach interessa tutti, perche' tutti devono
    // poter cifrare per lui. Quello di un atleta riguarda solo lo staff: agli
    // altri atleti direbbe soltanto chi si e' iscritto.
    if (certificate.success && certificate.data.role === "member") {
      return [self, ...peers.filter((peer) => peer.role !== "member")];
    }
    return [self, ...peers];
  }
  if (kind === "schedule.updated") {
    const subject = MemberScheduleSchema.safeParse(payload);
    return [
      self,
      ...peers.filter(
        (peer) => peer.role !== "member" || (subject.success && peer.id === subject.data.memberId),
      ),
    ];
  }
  return [self, ...peers];
}

/**
 * Il rifiuto di un envelope va detto in modo che il canale lo riconosca: e' una
 * decisione definitiva su *quel* messaggio, non un guasto di questo dispositivo.
 * Il client conferma comunque la ricezione, e cosi' un envelope che non
 * passera' mai smette di tornare a ogni riconnessione.
 */
function reject(reason: string): never {
  throw new RejectedEnvelopeError(reason);
}

/**
 * Il muro parlante di chi e' stato revocato: le modifiche non si accodano
 * verso pari che le scarterebbero in silenzio. Le schermate lo spiegano prima
 * di arrivare qui; questo e' il fallback per qualsiasi altra strada.
 */
function assertNotRevoked(profile: LocalProfile | undefined): void {
  if (profile?.revokedAccess) {
    throw new Error("Il coach ha revocato il tuo accesso: chiedi un nuovo invito per rientrare");
  }
}

/** Lo stesso, per cio' che arriva da fuori e non supera il proprio schema. */
function remote<T>(parse: () => T, reason: string): T {
  try {
    return parse();
  } catch {
    return reject(reason);
  }
}

/**
 * Il proprietario si riconosce dalla chiave, non dal sentito dire: il canale
 * della palestra e' derivato dalla chiave di firma del fondatore, quindi il
 * pari la cui chiave riproduce il `channelId` e' il proprietario — per
 * chiunque, senza certificati di mezzo. Serviva: `acceptInvite` registrava
 * l'invitante sempre come «coach», anche quando era il proprietario, e cosi'
 * la sua `role.revoked` — valida solo se firmata da un owner — veniva
 * scartata proprio dal dispositivo del revocato, che non vedeva mai il
 * cartello. Nessuno puo' fingersi proprietario: dovrebbe firmare con una
 * chiave che non ha.
 */
function isChannelAnchor(signingPublicKey: string, channelId: string | undefined): boolean {
  return channelId !== undefined && channelIdForOwner(signingPublicKey) === channelId;
}

function withOwnerRoles(profile: LocalProfile): LocalProfile {
  if (!profile.channelId) return profile;
  let changed = false;
  const peers = profile.peers.map((peer) => {
    if (peer.role === "owner" || !isChannelAnchor(peer.signingPublicKey, profile.channelId)) {
      return peer;
    }
    changed = true;
    return { ...peer, role: "owner" as const };
  });
  return changed ? { ...profile, peers } : profile;
}

function dedupePeers(peers: readonly AuthorizedPeer[]): AuthorizedPeer[] {
  // Il primo vince: un certificato che arriva dopo non puo' cambiare il ruolo
  // di un pari che conosciamo gia'.
  return peers.filter(
    (peer, index, all) => all.findIndex((candidate) => candidate.id === peer.id) === index,
  );
}

/** La data di oggi nel fuso della palestra, che non e' sempre quello del telefono. */
function todayInZone(timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((candidate) => candidate.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}`;
}

/**
 * Quanto vive un invito: quattordici giorni per entrambi i ruoli, e il link
 * corto scade insieme all'invito che custodisce — una data sola da mostrare e
 * da spiegare. Il costo va detto: un invito coach resta spendibile per due
 * settimane, e apre la vista nominativa su presenze e pianificazioni di tutti.
 * Lo rendono accettabile tre cose insieme: l'invito vale una volta sola,
 * «Ritira» spegne il link davvero (la voce sul relay viene cancellata), e gli
 * inviti in sospeso stanno in una schermata propria dove li si vede.
 */
const INVITE_LIFETIME_MS: Record<"member" | "coach", number> = {
  member: 14 * 24 * 60 * 60 * 1000,
  coach: 14 * 24 * 60 * 60 * 1000,
};

/** Ogni quanto, al massimo, si chiede allo staff di ripetere lo stato. */
const ALIGNMENT_INTERVAL_MS = 12 * 60 * 60 * 1000;

/**
 * Il freno del percorso urgente. `force` serve a chi sta ancora aspettando il
 * primo stato reale: la sua richiesta non puo' attendere cinque minuti. Ma
 * `awaitingInitialSync` resta vero finche' *qualcuno risponde*, e se lo staff e'
 * spento non risponde nessuno — allora ogni riautenticazione non deve far
 * partire una richiesta identica. Il limite è persistito per questo browser,
 * quindi una ricarica della pagina non lo azzera.
 */
const ALIGNMENT_FORCED_MIN_MS = 60 * 60 * 1000;

/**
 * La richiesta di allineamento non porta stato e non ha versioni: la seconda in
 * coda dice esattamente quello che dice la prima. Con questa chiave ne resta
 * una sola in attesa, quale che sia il numero di tentativi.
 */
const ALIGNMENT_DEDUPE_KEY = "sync.requested";

/** Finestra del digest di capienza: quanto in avanti guarda un atleta. */
const OCCUPANCY_WINDOW_DAYS = 35;

/** Una raffica di cambi produce un digest, non uno per cambio. */
const OCCUPANCY_DEBOUNCE_MS = 2_000;

/**
 * Il relay accetta blob fino a 64 KB: le pianificazioni si fermano molto prima,
 * cosi' identita', palestra e capienza hanno sempre spazio.
 */
const MAX_BACKUP_SCHEDULES_BYTES = 40_000;

/**
 * L'impronta di cio' che il backup **dice**, senza l'istante in cui e' stato
 * composto: `updatedAt` cambia a ogni composizione e renderebbe ogni backup
 * diverso dal precedente anche quando non lo e'. E' lo stesso schema che il
 * digest di capienza usa gia' in produzione (`occupancyFingerprint`).
 *
 * Le pianificazioni entrano gia' serializzate, cosi' come sono state misurate
 * per il controllo del budget: sono la parte che pesa, e ripercorrerle qui
 * sarebbe esattamente il lavoro doppio che PERF-10 chiedeva di togliere. Il
 * resto del payload si copia com'e', quindi un campo nuovo del backup entra
 * nell'impronta senza che nessuno debba ricordarsene.
 */
function backupFingerprint(payload: ProfileBackup, serializedSchedules?: string): string {
  const significant: Record<string, unknown> = { ...payload };
  delete significant.updatedAt;
  if (payload.schedules) significant.schedules = serializedSchedules;
  return canonicalJson(significant);
}

/**
 * Il lasciapassare che questo dispositivo puo' consegnare a un altro: un anello
 * nominativo per il destinatario, e nient'altro davanti.
 *
 * Lo consegna soltanto l'ancora del canale, cioe' chi ha fondato la palestra.
 * Un coach non ammette nessuno: il suo anello sarebbe un intermedio senza
 * `canIssue`, e una catena cosi' il relay la rifiuta — un lasciapassare che non
 * apre nulla e' peggio di nessun lasciapassare, perche' chi lo riceve lo scarta
 * senza sapere perche'.
 */
function grantFor(
  identity: IdentityMaterial,
  profile: LocalProfile,
  subject: PublicIdentity,
): ChannelProof | undefined {
  if (!isChannelAnchor(identity.signingPublicKey, profile.channelId)) return undefined;
  return [
    createChannelPass({
      issuer: identity,
      channelId: profile.channelId!,
      subjectId: subject.id,
      canIssue: false,
    }),
  ] as ChannelProof;
}

async function createAndQueueDomainChange(input: {
  identity: IdentityMaterial;
  profile: LocalProfile;
  kind: SignedOperation["kind"];
  payload: unknown;
  recipients: PublicIdentity[];
  authorization?: SignedInvite;
  channelPass?: ChannelProof;
  relay?: RelaySyncClient;
  /** Vedi `SyncQueueItem.dedupeKey`: solo per le richieste idempotenti. */
  dedupeKey?: string;
  replacementKey?: string;
  wake?: PushWake;
  deliveryMode?: "transient";
  /** Il chiamante inserisce l'envelope nella stessa transazione dei documenti. */
  deferQueue?: boolean;
}): Promise<SyncEnvelope> {
  if (!input.profile.channelId) {
    throw new Error("Profilo senza canale: serve un invito per tornare a sincronizzare");
  }
  if (input.kind === "sync.requested") {
    const gym = await loadGymDocuments(input.profile.gymId);
    input.payload = {
      ...SyncRequestSchema.parse(input.payload),
      knownDocuments: await alignmentInventory(
        input.profile,
        todayInZone(gym?.timeZone ?? "Europe/Rome"),
      ),
      needsChannelPass: needsChannelPass(
        input.profile,
        input.identity.signingPublicKey,
        input.identity.id,
      ),
    };
  }
  const operation = createSignedOperation({
    identity: input.identity,
    gymId: input.profile.gymId,
    role: input.profile.role,
    kind: input.kind,
    payload: input.payload,
  });
  const envelope = encryptEnvelope({
    sender: input.identity,
    channelId: input.profile.channelId,
    payload: {
      operation,
      author: toPublicIdentity(input.identity),
      ...(input.authorization ? { authorization: input.authorization } : {}),
      ...(input.channelPass ? { channelPass: input.channelPass } : {}),
    } satisfies DomainMessage,
    recipients: input.recipients,
    expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
  });
  if (input.kind === "sync.requested" && !input.replacementKey) {
    input.replacementKey = opaqueChannelId(
      canonicalJson([
        "request",
        input.profile.channelId,
        input.identity.id,
        await ensureDeviceId(),
        input.recipients.map((peer) => peer.id).sort(),
      ]),
    );
  }
  if (input.deferQueue) return envelope;
  if (input.relay)
    await input.relay.publish(
      envelope,
      input.dedupeKey,
      input.wake,
      input.deliveryMode,
      input.replacementKey,
    );
  else {
    await enqueue({
      envelope,
      attempts: 0,
      nextAttemptAt: new Date().toISOString(),
      ...(input.dedupeKey ? { dedupeKey: input.dedupeKey } : {}),
      ...(input.replacementKey ? { replacement: { key: input.replacementKey, version: 1 } } : {}),
      ...(input.wake ? { wake: input.wake } : {}),
      ...(input.deliveryMode ? { deliveryMode: input.deliveryMode } : {}),
    });
  }
  return envelope;
}

async function syncRecipientStatus(
  identity: IdentityMaterial,
  profile: LocalProfile,
  relay: RelaySyncClient,
  subjectId: string,
  active: boolean,
  changedAt: string,
): Promise<void> {
  if (profile.role !== "owner" || !profile.channelId || subjectId === identity.id) return;
  await relay.setRecipientStatus(
    createRecipientStatus({
      owner: identity,
      channelId: profile.channelId,
      subjectId,
      active,
      changedAt,
    }),
  );
}

const PUSH_WAKE_GRANT_LIFETIME_MS = 180 * 24 * 60 * 60 * 1000;
const PUSH_WAKE_GRANT_RENEWAL_MS = 30 * 24 * 60 * 60 * 1000;

function issuedPushWakeGrantKey(channelId: string, subjectId: string): string {
  return `issuedPushWakeGrant:${channelId}:${subjectId}`;
}

/**
 * Porta sulla nuova capability anche i coach invitati prima dell'introduzione
 * delle push. Il marker locale evita di generare una nuova operazione a ogni
 * riconnessione; l'ultimo mese di validità è riservato al rinnovo anticipato.
 */
async function ensureCoachPushWakeGrants(
  owner: IdentityMaterial,
  profile: LocalProfile,
  relay: RelaySyncClient,
): Promise<void> {
  if (
    profile.role !== "owner" ||
    !profile.channelId ||
    !isChannelAnchor(owner.signingPublicKey, profile.channelId)
  )
    return;
  const now = Date.now();
  for (const coach of profile.peers.filter((peer) => peer.role === "coach")) {
    const key = issuedPushWakeGrantKey(profile.channelId, coach.id);
    const existing = await getMeta<PushWakeGrant>(key);
    if (
      existing?.channelId === profile.channelId &&
      existing.subjectId === coach.id &&
      verifyPushWakeGrant(existing, new Date(now + PUSH_WAKE_GRANT_RENEWAL_MS).toISOString())
    )
      continue;
    const grant = createPushWakeGrant({
      owner,
      channelId: profile.channelId,
      subjectId: coach.id,
      expiresAt: new Date(now + PUSH_WAKE_GRANT_LIFETIME_MS).toISOString(),
    });
    await createAndQueueDomainChange({
      identity: owner,
      profile,
      kind: "push.wake.granted",
      payload: grant,
      recipients: [toPublicIdentity(owner), coach],
      relay,
    });
    await setMeta(key, grant);
  }
}

/**
 * Connessione usa e getta: serve solo a farsi riconoscere dal relay e chiedere
 * il proprio blob. Viene chiusa comunque vada, anche in errore.
 */
function fetchRemoteBackup(
  identity: IdentityMaterial,
  relayUrl: string,
  timeoutMs = 12_000,
): Promise<string | null> {
  return new Promise<string | null>((resolve, reject) => {
    let settled = false;
    const finish = (outcome: () => void) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      client.close();
      outcome();
    };
    const timer = window.setTimeout(
      () => finish(() => reject(new Error("Relay non raggiungibile"))),
      timeoutMs,
    );
    const client = new RelaySyncClient({
      url: relayUrl,
      // La casella personale: il relay la ricalcola dall'identita' che ha
      // superato la challenge, quindi non serve — e non esiste — un
      // lasciapassare da esibire. Chi rientra con le sole 12 parole non sa
      // ancora quale sia la sua palestra.
      channelId: recoveryChannelId(identity.id),
      identity,
      onEnvelope: () => Promise.resolve(),
      onState: (snapshot) => {
        if (settled) return;
        // Sonda usa e getta, non un canale da tenere vivo: al primo tentativo
        // fallito si risponde subito, invece di far fissare all'utente una
        // schermata di attesa mentre il backoff riprova per conto suo.
        if (snapshot.state === "unreachable" || snapshot.state === "offline") {
          finish(() => reject(new Error("Relay non raggiungibile")));
          return;
        }
        // Solo questi due stati provano che la challenge e' stata superata.
        if (snapshot.state !== "synced" && snapshot.state !== "syncing") return;
        void client.getBackup().then(
          (blob) => finish(() => resolve(blob)),
          (error: unknown) =>
            finish(() =>
              reject(
                error instanceof Error ? error : new Error("Richiesta di backup non riuscita"),
              ),
            ),
        );
      },
    });
    client.reconnectNow();
  });
}

function defaultSlots() {
  return Array.from({ length: 7 }, (_, index) => ({
    id: `slot_day_${index + 1}_1830`,
    weekday: index + 1,
    startTime: "18:30",
    durationMinutes: 60,
    capacity: 10,
    active: true,
  }));
}

async function loadState(): Promise<StoreState> {
  const identityRecord = await loadIdentity();
  const storedProfile = await getMeta<LocalProfile>("profile");
  const acceptedInvite = await getMeta<SignedInvite>("acceptedInvite");
  const profile = storedProfile
    ? withOwnerRoles({
        ...storedProfile,
        /**
         * Un profilo di fondatore nato prima della prova di appartenenza
         * ritrova da solo il proprio canale: e' derivato dalla sua chiave, e la
         * chiave non e' cambiata. Chi e' entrato con un invito, invece, non ha
         * modo di ricostruire un lasciapassare che non ha mai avuto, e dovra'
         * farsene dare uno nuovo.
         */
        ...(storedProfile.channelId || storedProfile.role !== "owner" || !identityRecord
          ? {}
          : { channelId: channelIdForOwner(identityRecord.identity.signingPublicKey) }),
        peers: storedProfile.peers.map((peer) => ({
          ...peer,
          role:
            peer.role ??
            (peer.id === identityRecord?.identity.id
              ? storedProfile.role
              : peer.id === acceptedInvite?.payload.issuedBy
                ? "coach"
                : "member"),
        })),
      })
    : undefined;
  const gymDocument = profile ? await loadGymDocuments(profile.gymId) : undefined;
  const scheduleIds = (await getMeta<string[]>("scheduleIds")) ?? [];
  const schedules = (
    await Promise.all(
      scheduleIds.map((id) =>
        profile ? loadMemberScheduleDocument(profile.gymId, id) : Promise.resolve(undefined),
      ),
    )
  ).filter((schedule): schedule is NonNullable<typeof schedule> => Boolean(schedule));
  const coachProjection = profile ? await loadCoachProjectionDocument(profile.gymId) : undefined;
  const occupancy = profile ? await loadOccupancyDocument(profile.gymId) : undefined;
  const broadcastState = profile
    ? await loadBroadcastState(profile.gymId)
    : { broadcasts: [], manifests: [], receipts: [] };
  const awaitingInitialSync = (await getMeta<boolean>("awaitingInitialSync")) ?? false;
  const invites = liveInvites(
    (await getMeta<IssuedInvite[]>("issuedInvites")) ?? [],
    profile?.spentInvites ?? [],
  );
  const lastStaffContactAt = await getMeta<string>("lastStaffContactAt");
  const pushSubscribed =
    "serviceWorker" in navigator &&
    typeof navigator.serviceWorker.getRegistration === "function" &&
    typeof Notification !== "undefined"
      ? Boolean(
          await (await navigator.serviceWorker.getRegistration())?.pushManager.getSubscription(),
        )
      : false;
  return {
    ready: true,
    ...(identityRecord ? { identity: identityRecord.identity } : {}),
    backupConfirmed: identityRecord?.backupConfirmed ?? false,
    ...(profile ? { profile } : {}),
    ...(gymDocument ? { gym: normalizeGym(GymSchema.parse(gymDocument)) } : {}),
    schedules: schedules.map((document) =>
      normalizeMemberSchedule(MemberScheduleSchema.parse(document)),
    ),
    ...(occupancy ? { occupancy: OccupancyDigestSchema.parse(occupancy) } : {}),
    alerts: coachProjection ? [...coachProjection.alerts] : [],
    broadcasts: broadcastState.broadcasts,
    broadcastManifests: broadcastState.manifests,
    broadcastReceipts: broadcastState.receipts,
    notificationPermission:
      typeof Notification === "undefined" ? "unsupported" : Notification.permission,
    pushSubscribed,
    invites,
    awaitingInitialSync,
    ...(acceptedInvite ? { acceptedInviteId: acceptedInvite.payload.id } : {}),
    ...(lastStaffContactAt ? { lastStaffContactAt } : {}),
    sync: emptySync,
  };
}

/**
 * Per quanto si tiene memoria di un invito speso. Serve finche' quell'invito
 * potrebbe ancora essere rigiocato, cioe' finche' non scade: il piu' longevo
 * dura sette giorni, quindi trenta sono un margine largo. Senza questa potatura
 * l'elenco crescerebbe di una voce per ogni persona mai entrata, e crescerebbe
 * dentro il backup — che ha un tetto di 64 KB, e che perdere sarebbe peggio.
 * Le revoche invece non si potano mai: un'identita' revocata resta revocata.
 */
const SPENT_INVITE_RETENTION_MS = 30 * 24 * 60 * 60 * 1000;

function withoutStaleConsumptions(profile: LocalProfile, now = Date.now()): LocalProfile {
  const spent = profile.spentInvites;
  if (!spent?.length) return profile;
  const kept = spent.filter(
    (entry) => now - Date.parse(entry.recordedAt) < SPENT_INVITE_RETENTION_MS,
  );
  return kept.length === spent.length ? profile : { ...profile, spentInvites: kept };
}

/**
 * Quelli che hanno ancora senso: un invito scaduto non apre nulla e un invito
 * speso e' gia' diventato una persona. Mostrarli sarebbe offrire un comando —
 * «ritira» — che non cambierebbe niente.
 */
function liveInvites(
  issued: readonly IssuedInvite[],
  spent: readonly InviteConsumption[],
  now = Date.now(),
): IssuedInvite[] {
  return issued.filter(
    (invite) =>
      Date.parse(invite.expiresAt) > now && !spent.some((entry) => entry.inviteId === invite.id),
  );
}

export function AppStoreProvider({ children }: PropsWithChildren) {
  const [state, setState] = useState<StoreState>({
    ready: false,
    backupConfirmed: false,
    schedules: [],
    alerts: [],
    broadcasts: [],
    broadcastManifests: [],
    broadcastReceipts: [],
    notificationPermission:
      typeof Notification === "undefined" ? "unsupported" : Notification.permission,
    pushSubscribed: false,
    invites: [],
    awaitingInitialSync: false,
    sync: emptySync,
  });
  const relayRef = useRef<RelaySyncClient | undefined>(undefined);
  const stateRef = useRef(state);
  stateRef.current = state;
  const backupTimerRef = useRef<number | undefined>(undefined);
  /** Copia in memoria del flag su disco: interrogarlo a ogni stato costerebbe una lettura. */
  const backupDirtyRef = useRef(false);
  /**
   * L'impronta dell'ultimo backup **partito davvero**. Vive in memoria e non su
   * disco: un invio non riuscito non deve poter essere scambiato per fatto, e
   * dopo un riavvio dell'app la prima modifica ne rispedisce comunque uno —
   * che e' il comportamento sicuro se nel frattempo il relay ha perso la copia.
   */
  const sentBackupRef = useRef<string | undefined>(undefined);
  const alignmentRef = useRef(0);
  /** Invalida le callback appartenenti al profilo che viene azzerato. */
  const profileGenerationRef = useRef(0);
  /** Il riallineamento può attraversare più transazioni IndexedDB. */
  const alignmentTaskRef = useRef<Promise<void> | undefined>(undefined);
  const occupancyTimerRef = useRef<number | undefined>(undefined);
  /**
   * Gli envelope in arrivo si applicano uno alla volta. Il relay ne consegna
   * l'intera giacenza in raffica e ogni merge parte da cio' che c'e' su disco:
   * lasciarli lavorare in parallelo farebbe leggere a due di loro lo stesso
   * stato di partenza, e il secondo cancellerebbe il primo.
   */
  const applyQueueRef = useRef<Promise<unknown>>(Promise.resolve());
  const [deviceId, setDeviceId] = useState<string>();
  /** Anche in un ref: le callback stabili non possono dipendere dallo stato. */
  const deviceIdRef = useRef<string | undefined>(undefined);

  /** Serializza l'applicazione degli envelope mantenendone gli errori al chiamante. */
  const enqueueApply = useCallback(<T,>(task: () => Promise<T>): Promise<T> => {
    const next = applyQueueRef.current.then(task, task);
    applyQueueRef.current = next.catch(() => undefined);
    return next;
  }, []);

  const reload = useCallback(async () => {
    try {
      setState((previous) => ({ ...previous, ready: false }));
      const loaded = await loadState();
      setState((previous) => ({ ...loaded, sync: previous.sync }));
    } catch (error) {
      setState((previous) => ({ ...previous, ready: true, error: errorMessage(error) }));
    }
  }, []);

  useEffect(() => {
    void reload();
    const onMessage = (event: MessageEvent<string>) => {
      if (event.data === "profile-removing") {
        relayRef.current?.close();
        closeLocalDatabase();
        return;
      }
      if (event.data === "profile-removed") {
        setState({
          ready: true,
          backupConfirmed: false,
          schedules: [],
          alerts: [],
          broadcasts: [],
          broadcastManifests: [],
          broadcastReceipts: [],
          notificationPermission:
            typeof Notification === "undefined" ? "unsupported" : Notification.permission,
          pushSubscribed: false,
          invites: [],
          awaitingInitialSync: false,
          sync: emptySync,
        });
        return;
      }
      void reload();
    };
    channel?.addEventListener("message", onMessage);
    // Il flag sopravvive alla chiusura dell'app: un backup rimasto indietro ieri
    // sera va spedito comunque alla prima autenticazione di stamattina.
    void getMeta<boolean>("backupDirty").then((dirty) => {
      backupDirtyRef.current = dirty ?? false;
    });
    void ensureDeviceId().then((value) => {
      deviceIdRef.current = value;
      setDeviceId(value);
    });
    return () => {
      channel?.removeEventListener("message", onMessage);
      if (backupTimerRef.current) window.clearTimeout(backupTimerRef.current);
    };
  }, [reload]);

  /**
   * Il backup insegue lo stato, non lo precede: si compone da `stateRef` al
   * momento dell'invio, cosi' una raffica di modifiche produce un solo blob
   * aggiornato invece di N versioni intermedie.
   */
  const uploadBackup = useCallback(async () => {
    const current = stateRef.current;
    if (!current.identity || !current.profile) return;
    const acceptedInvite = await getMeta<SignedInvite>("acceptedInvite");
    const ownSchedule = current.schedules.find(
      (schedule) => schedule.memberId === current.identity!.id,
    );
    /**
     * Un coach che apre l'app su un secondo dispositivo deve vedere le presenze
     * anche se nessun altro dispositivo e' collegato in quel momento. Le
     * pianificazioni entrano nel backup solo se ci stanno: oltre il tetto il
     * relay rifiuterebbe il blob, e perdere il backup sarebbe peggio che
     * perdere questa comodita'.
     */
    const staffSchedules = current.profile.role === "member" ? undefined : current.schedules;
    /**
     * `canonicalJson` e non `JSON.stringify`: e' la stessa forma che la
     * cifratura serializzera' comunque, quindi la misura del budget e
     * l'impronta si pagano una volta sola invece di due (PERF-10).
     */
    const serializedSchedules = staffSchedules && canonicalJson(staffSchedules);
    const withinBudget =
      serializedSchedules !== undefined && serializedSchedules.length <= MAX_BACKUP_SCHEDULES_BYTES;
    const payload = ProfileBackupSchema.parse({
      schemaVersion: 1,
      profile: current.profile,
      ...(acceptedInvite ? { acceptedInvite } : {}),
      ...(current.gym ? { gym: current.gym } : {}),
      ...(ownSchedule ? { ownSchedule } : {}),
      ...(withinBudget ? { schedules: staffSchedules } : {}),
      // Solo per gli atleti: e' l'unico modo perche' la capienza sia leggibile
      // su un dispositivo nuovo prima che un coach torni online.
      ...(current.occupancy ? { occupancy: current.occupancy } : {}),
      // Gli inviti in sospeso seguono l'identita', non il dispositivo: e' cio'
      // che rende «Ritira» e «Mostra» comandi su cui si puo' contare ovunque.
      ...(current.invites.length > 0 ? { issuedInvites: current.invites } : {}),
      updatedAt: new Date().toISOString(),
    });
    /**
     * Lo stesso schema del digest di capienza (`occupancyFingerprint`): si
     * confronta cio' che il backup **dice**, non l'istante in cui e' stato
     * composto. Senza questo confronto ogni salvataggio ricifrava e rispediva
     * per intero decine di KB, anche quando il contenuto era identico a quello
     * gia' custodito dal relay.
     */
    const fingerprint = backupFingerprint(payload, serializedSchedules);
    if (fingerprint === sentBackupRef.current) return;
    const sent = relayRef.current?.putBackup(encryptProfileBackup(current.identity, payload));
    // Solo cio' che e' partito: un invio fallito deve poter essere ritentato
    // identico, e il flag `backupDirty` da solo non lo garantirebbe.
    sentBackupRef.current = sent === true ? fingerprint : undefined;
    backupDirtyRef.current = sent !== true;
    await setMeta("backupDirty", backupDirtyRef.current);
  }, []);

  const scheduleBackupUpload = useCallback(() => {
    if (backupTimerRef.current) window.clearTimeout(backupTimerRef.current);
    backupTimerRef.current = window.setTimeout(() => {
      backupTimerRef.current = undefined;
      void uploadBackup().catch(() => {
        // Un backup non riuscito non e' un errore per l'utente: si ritenta al
        // prossimo canale autenticato.
        backupDirtyRef.current = true;
      });
    }, BACKUP_DEBOUNCE_MS);
  }, [uploadBackup]);

  const persistProfile = useCallback(
    async (profile: LocalProfile) => {
      const pruned = withoutStaleConsumptions(profile);
      await setMeta("profile", pruned);
      // Un invito appena speso deve sparire subito da «Inviti in sospeso»:
      // il filtro al solo avvio lo farebbe restare li' finche' la PWA vive.
      setState((previous) => ({
        ...previous,
        profile: pruned,
        invites: liveInvites(previous.invites, pruned.spentInvites ?? []),
      }));
      scheduleBackupUpload();
    },
    [scheduleBackupUpload],
  );

  const persistSchedule = useCallback(
    async (schedule: MemberSchedule, alreadyStored = false) => {
      const parsed = MemberScheduleSchema.parse(schedule);
      if (!alreadyStored) await saveMemberScheduleDocument(parsed);
      const currentIds = (await getMeta<string[]>("scheduleIds")) ?? [];
      await setMeta("scheduleIds", [...new Set([...currentIds, parsed.memberId])]);
      setState((previous) => ({
        ...previous,
        schedules: [
          ...previous.schedules.filter((item) => item.memberId !== parsed.memberId),
          parsed,
        ],
      }));
      channel?.postMessage("schedule");
      scheduleBackupUpload();
    },
    [scheduleBackupUpload],
  );

  /**
   * L'uscita di una pianificazione dal calendario. La revoca che si limitava a
   * togliere il pari lasciava le sue prenotazioni nel roster degli slot e nei
   * conteggi di capienza, congelate all'ultimo stato ricevuto: il coach
   * continuava a contare presenze di chi non poteva piu' venire.
   */
  const removeSchedule = useCallback(
    async (gymId: string, memberId: string) => {
      await removeMemberScheduleDocument(gymId, memberId);
      const currentIds = (await getMeta<string[]>("scheduleIds")) ?? [];
      await setMeta(
        "scheduleIds",
        currentIds.filter((id) => id !== memberId),
      );
      setState((previous) => ({
        ...previous,
        schedules: previous.schedules.filter((schedule) => schedule.memberId !== memberId),
      }));
      channel?.postMessage("schedule");
      scheduleBackupUpload();
    },
    [scheduleBackupUpload],
  );

  const persistGym = useCallback(
    async (gym: Gym) => {
      const normalized = normalizeGym(gym);
      await saveGymDocuments(normalized);
      setState((previous) => ({ ...previous, gym: normalized }));
      scheduleBackupUpload();
      return normalized;
    },
    [scheduleBackupUpload],
  );

  const persistOccupancy = useCallback(async (digest: OccupancyDigest) => {
    const timeZone = stateRef.current.gym?.timeZone;
    // Le giornate passate non servono a nessuno e farebbero crescere il digest
    // senza limite: si potano appena arriva.
    const normalized = normalizeOccupancyDigest(
      timeZone ? pruneOccupancyDigest(digest, todayInZone(timeZone)) : digest,
    );
    await saveOccupancyDocument(normalized);
    setState((previous) => ({ ...previous, occupancy: normalized }));
    return normalized;
  }, []);

  const persistBroadcast = useCallback(
    async (
      message: BroadcastMessage,
      sourceEnvelopeId: string,
      alreadyStored = false,
      publishedAt?: string,
    ) => {
      if (!alreadyStored) await saveBroadcastDocument(message, sourceEnvelopeId, publishedAt);
      const stored: StoredBroadcastDocument = {
        message,
        sourceEnvelopeId,
        ...(publishedAt ? { publishedAt } : {}),
      };
      setState((previous) => ({
        ...previous,
        broadcasts: [
          stored,
          ...previous.broadcasts.filter((entry) => entry.message.id !== message.id),
        ]
          .sort((left, right) => right.message.createdAt.localeCompare(left.message.createdAt))
          .slice(0, 100),
      }));
      return stored;
    },
    [],
  );

  const persistBroadcastManifest = useCallback(
    async (manifest: BroadcastManifest, alreadyStored = false) => {
      if (!alreadyStored) await saveBroadcastManifestDocument(manifest);
      setState((previous) => ({
        ...previous,
        broadcastManifests: [
          manifest,
          ...previous.broadcastManifests.filter(
            (entry) => entry.broadcastId !== manifest.broadcastId,
          ),
        ],
      }));
    },
    [],
  );

  const persistBroadcastReceipt = useCallback(
    async (receipt: BroadcastReceipt, storedProjection?: BroadcastReceiptProjection) => {
      const projection = storedProjection ?? (await saveBroadcastReceiptDocument(receipt));
      setState((previous) => ({
        ...previous,
        broadcastReceipts: [
          projection,
          ...previous.broadcastReceipts.filter(
            (entry) =>
              entry.broadcastId !== projection.broadcastId ||
              entry.memberId !== projection.memberId,
          ),
        ],
      }));
      return projection;
    },
    [],
  );

  const queueDomainChange = useCallback(
    async (
      kind: SignedOperation["kind"],
      payload: unknown,
      explicitRecipients?: PublicIdentity[],
      profileOverride?: LocalProfile,
    ) => {
      const current = stateRef.current;
      if (!current.identity || !current.profile) return;
      const profile = profileOverride ?? current.profile;
      const recipients = (
        explicitRecipients ??
        recipientsFor(kind, payload, toPublicIdentity(current.identity), profile.peers)
      ).filter(
        (peer, index, all) => all.findIndex((candidate) => candidate.id === peer.id) === index,
      );
      const authorization = await getMeta<SignedInvite>("acceptedInvite");
      await createAndQueueDomainChange({
        identity: current.identity,
        profile,
        kind,
        payload,
        recipients,
        ...(authorization ? { authorization } : {}),
        ...(relayRef.current ? { relay: relayRef.current } : {}),
      });
    },
    [],
  );

  /**
   * Anti-entropia: la casella del relay e' affidabile ma non eterna. Un
   * dispositivo rimasto spento a lungo non puo' dipendere solo da cio' che ha
   * trovato in giacenza, quindi al primo canale autenticato chiede allo staff di
   * ripetere lo stato. Senza freno diventerebbe una richiesta a ogni cambio di
   * rete, quindi si limita a una ogni pochi minuti — e anche `force`, che serve
   * a chi aspetta il primo stato, ha il suo minimo: vedi
   * `ALIGNMENT_FORCED_MIN_MS`.
   */
  const requestAlignment = useCallback(async (force = false, expectedGeneration: number) => {
    if (profileGenerationRef.current !== expectedGeneration) return;
    const identity = stateRef.current.identity;
    const profile = await getMeta<LocalProfile>("profile");
    if (profileGenerationRef.current !== expectedGeneration) return;
    if (!identity || !profile) return;
    // Lo staff scarterebbe comunque la richiesta di un revocato: chiedere
    // l'allineamento sarebbe rumore che non puo' ricevere risposta.
    if (profile.revokedAccess) return;
    const now = Date.now();
    // Il minimo vale per tutti: `force` accorcia l'attesa, non la toglie.
    const wait = force ? ALIGNMENT_FORCED_MIN_MS : ALIGNMENT_INTERVAL_MS;
    const marker = `alignmentRequested:${profile.channelId}`;
    const previous = Math.max(alignmentRef.current, (await getMeta<number>(marker)) ?? 0);
    if (now - previous < wait) return;
    /**
     * Se chi chiede e' staff, fra i possibili interlocutori c'e' anche l'altro
     * proprio dispositivo: e' la stessa identita', quindi il relay gli consegna
     * l'envelope, e nessuno conosce la palestra meglio di lui. Il `deviceId`
     * nella richiesta serve appunto a non rispondere alla propria.
     */
    const staff = profile.peers.filter(
      (peer) => peer.role !== "member" && (profile.role !== "member" || peer.id !== identity.id),
    );
    if (staff.length === 0) return;
    alignmentRef.current = now;
    const authorization = await getMeta<SignedInvite>("acceptedInvite");
    if (profileGenerationRef.current !== expectedGeneration) return;
    await createAndQueueDomainChange({
      identity,
      profile,
      kind: "sync.requested",
      payload: {
        requesterId: identity.id,
        requestedAt: new Date().toISOString(),
        ...(deviceIdRef.current ? { deviceId: deviceIdRef.current } : {}),
      },
      recipients: staff,
      dedupeKey: ALIGNMENT_DEDUPE_KEY,
      ...(authorization ? { authorization } : {}),
      ...(relayRef.current ? { relay: relayRef.current } : {}),
    });
    await setMeta(marker, now);
  }, []);

  const relayIdentity = state.identity;
  const relayHasProfile = Boolean(state.profile);
  const relayChannelId = state.profile?.channelId;
  /**
   * La catena entra fra le dipendenze in forma stabile: un lasciapassare nuovo
   * — quello nominativo che arriva dallo staff — deve riaprire il canale, un
   * render qualunque no.
   */
  const relayProofKey = state.profile?.channelProof
    ? JSON.stringify(state.profile.channelProof)
    : undefined;

  useEffect(() => {
    const relayGeneration = profileGenerationRef.current;
    relayRef.current?.close();
    relayRef.current = undefined;
    if (!relayIdentity || !deviceId) return;
    /**
     * Chi ha gia' ricevuto il rifiuto del proprio invito. Il fantasma ritenta —
     * la sua coda non sa di essere scartata — e una risposta per envelope
     * sarebbe una raffica: il primo rifiuto basta, e la sua app da quel momento
     * smette di parlare. Vive quanto la connessione, come i suoi effetti.
     */
    const rejectionsSent = new Set<string>();
    if (relayHasProfile && !relayChannelId) {
      // Profilo precedente alla prova di appartenenza: il relay non ha modo di
      // riconoscerlo, e riprovare non cambierebbe nulla.
      setState((previous) => ({
        ...previous,
        sync: {
          state: "unavailable",
          pending: previous.sync.pending,
          message: "Serve un nuovo invito per tornare a sincronizzare",
        },
      }));
      return;
    }
    if (!relayChannelId) return;
    const runtimeConfig = readRuntimeConfig();
    const relayUrl = resolveRelayUrl({
      runtimeValue: runtimeConfig,
      location,
      development: import.meta.env.DEV,
      developmentUrl: import.meta.env.VITE_RELAY_URL as string | undefined,
    });
    if (!relayUrl || typeof WebSocket === "undefined") {
      setState((previous) => ({
        ...previous,
        sync: {
          // La configurazione mancante non dipende dalla rete: riprovare non serve.
          state: "unavailable",
          pending: previous.sync.pending,
          message: relayUrl
            ? "WebSocket non disponibile in questo browser"
            : "Sincronizzazione relay non configurata",
        },
      }));
      return;
    }
    const relay = new RelaySyncClient({
      url: relayUrl,
      channelId: relayChannelId,
      ...(relayProofKey ? { channelProof: JSON.parse(relayProofKey) as ChannelProof } : {}),
      identity: relayIdentity,
      ...(deviceId ? { deviceId } : {}),
      onState: (sync) => {
        if (profileGenerationRef.current !== relayGeneration) return;
        setState((previous) => ({ ...previous, sync }));
        reportSimSyncState(sync.state);
        // Il canale autenticato e' l'unico momento in cui un backup rimasto
        // indietro puo' partire: qui c'e' la certezza che esista.
        const authenticated = sync.state === "synced" || sync.state === "syncing";
        if (authenticated && backupDirtyRef.current) void uploadBackup().catch(() => undefined);
      },
      onPushConfig: (pushPublicKey) => {
        if (profileGenerationRef.current !== relayGeneration) return;
        setState((previous) => {
          const next = { ...previous };
          if (pushPublicKey) next.pushPublicKey = pushPublicKey;
          else delete next.pushPublicKey;
          return next;
        });
      },
      onPublished: async (envelopeId) => {
        if (profileGenerationRef.current !== relayGeneration) return;
        const gymId = stateRef.current.profile?.gymId;
        if (!gymId) return;
        const publishedAt = new Date().toISOString();
        const broadcastId = await markBroadcastEnvelopePublished(gymId, envelopeId, publishedAt);
        if (!broadcastId) return;
        setState((previous) => ({
          ...previous,
          broadcasts: previous.broadcasts.map((entry) =>
            entry.message.id === broadcastId ? { ...entry, publishedAt } : entry,
          ),
        }));
      },
      onAuthenticated: () => {
        if (profileGenerationRef.current !== relayGeneration) return;
        const activeProfile = stateRef.current.profile;
        if (activeProfile) {
          void ensureCoachPushWakeGrants(relayIdentity, activeProfile, relay).catch(
            () => undefined,
          );
        }
        const task = requestAlignment(stateRef.current.awaitingInitialSync, relayGeneration).catch(
          () => undefined,
        );
        alignmentTaskRef.current = task;
        if (typeof Notification !== "undefined" && Notification.permission === "granted") {
          void navigator.serviceWorker?.ready.then(async (registration) => {
            const subscription = await registration.pushManager.getSubscription();
            if (subscription) {
              const parsed = WebPushSubscriptionSchema.safeParse(subscription.toJSON());
              if (parsed.success && relay.subscribePush(parsed.data)) {
                setState((previous) => ({ ...previous, pushSubscribed: true }));
              }
            }
          });
        }
        void getMeta<ReturnType<typeof createPushWakeRevocation>[]>(
          "pendingPushWakeRevocations",
        ).then((revocations) => {
          for (const revocation of revocations ?? []) relay.revokePushGrant(revocation);
        });
      },
      onEnvelope: (envelope) => {
        if (profileGenerationRef.current !== relayGeneration) return Promise.resolve();
        return enqueueApply(async () => {
          const identity = relayIdentity;
          // Il profilo si rilegge dal disco a ogni envelope: lo stato di React
          // arriva un render dopo, e una raffica di envelope leggerebbe due volte
          // la stessa fotografia. Il riconoscimento del proprietario va rifatto
          // qui, sul dato del disco: un profilo scritto prima di questa regola
          // ha il fondatore segnato «coach», e con quel ruolo la sua revoca
          // verrebbe scartata — e confermata al relay, cioe' persa per sempre.
          const stored = await getMeta<LocalProfile>("profile");
          if (!stored) return;
          const profile = withOwnerRoles(stored);
          const message = remote(
            () => decryptEnvelope<DomainMessage>(envelope, identity),
            "Envelope non decifrabile",
          );
          if (message.author.id !== envelope.senderId) reject("Autore envelope non valido");
          if (
            !verifySignedOperation(
              message.operation,
              message.author.signingPublicKey,
              profile.gymId,
            )
          ) {
            reject("Operazione remota non valida");
          }
          const wasKnown = profile.peers.some((peer) => peer.id === message.author.id);
          const authorizedAuthor = resolveOperationAuthorization({
            operation: message.operation,
            author: message.author,
            knownPeers: profile.peers,
            expectedGymId: profile.gymId,
            ...(message.authorization ? { invitation: message.authorization } : {}),
            // Chi e' stato revocato non rientra, e un invito gia' speso non fa
            // entrare nessun altro: sono le due cose che questo dispositivo
            // ricorda e che nessuna firma altrui puo' disfare.
            ...(profile.revokedPeers ? { revokedPeers: profile.revokedPeers } : {}),
            ...(profile.spentInvites ? { spentInvites: profile.spentInvites } : {}),
            // Chi puo' invitare si legge dalla chiave da cui il canale e'
            // derivato, non dal ruolo che gli abbiamo attribuito noi.
            ...(profile.channelId ? { expectedChannelId: profile.channelId } : {}),
          });
          if (!authorizedAuthor) {
            /**
             * Prima di scartare in silenzio: se il motivo e' che l'invito
             * presentato e' gia' stato speso da un altro \u2014 o ritirato \u2014 chi lo
             * ha presentato ha diritto di saperlo. Senza questa riga restava
             * \u00abin attesa del coach\u00bb per sempre: profilo creato, calendario
             * visibile, prenotazioni che non arrivavano a nessuno. Lo dice chi
             * accoglie, perche' e' l'unico che tiene l'elenco degli spesi.
             */
            const presented = message.authorization;
            const spent =
              presented &&
              (profile.spentInvites ?? []).find(
                (entry) => entry.gymId === profile.gymId && entry.inviteId === presented.payload.id,
              );
            const notice = `${spent?.inviteId ?? ""}:${message.author.id}`;
            if (
              spent &&
              spent.consumedBy !== message.author.id &&
              profile.role !== "member" &&
              !rejectionsSent.has(notice)
            ) {
              rejectionsSent.add(notice);
              const rejection = recordInviteRejection({
                rejecter: identity,
                gymId: profile.gymId,
                inviteId: spent.inviteId,
                subjectId: message.author.id,
                // Senza `consumedBy` l'invito non ha fatto entrare nessuno:
                // e' stato ritirato, ed e' un'altra frase.
                reason: spent.consumedBy ? "spent" : "withdrawn",
              });
              await createAndQueueDomainChange({
                identity,
                profile,
                kind: "invite.rejected",
                payload: rejection,
                recipients: [message.author],
                relay,
              }).catch(() => undefined);
            }
            reject("Ruolo non autorizzato per l\u2019operazione remota");
          }
          let peers = dedupePeers([...profile.peers, authorizedAuthor]);
          let revokedPeers = profile.revokedPeers ?? [];
          let spentInvites = profile.spentInvites ?? [];
          /** Il rifiuto del proprio invito, se questo envelope lo porta. */
          let rejectedAccess = profile.rejectedAccess;
          /** La revoca di questo envelope che riguarda proprio questa identita'. */
          let selfRevocation: RoleRevocation | undefined;
          const operation = message.operation;

          /**
           * Se l'autore risulta autorizzato nonostante revoche a suo carico,
           * le ha superate con un invito emesso dopo (`authorization.ts`):
           * quelle revoche sono storia chiusa. Tenerle bloccherebbe il suo
           * certificato di ruolo e, sugli altri dispositivi, ogni suo
           * messaggio futuro — cioe' il rientro che l'invito nuovo promette.
           */
          if (message.authorization) {
            const supersededBefore = Date.parse(message.authorization.payload.issuedAt);
            revokedPeers = revokedPeers.filter(
              (revocation) =>
                revocation.subjectId !== message.author.id ||
                Date.parse(revocation.revokedAt) >= supersededBefore,
            );
          }

          let recipientStatus:
            { subjectId: string; active: boolean; changedAt: string } | undefined;
          if (operation.kind === "role.issued") {
            const certificate = remote(
              () => RoleCertificateSchema.parse(operation.payload),
              "Certificato di ruolo non valido",
            );
            const issuer = peers.find((peer) => peer.id === certificate.issuedBy);
            /**
             * Un certificato che nomina un coach — o un proprietario — e' un
             * titolo, e i titoli li da' chi ha fondato la palestra: e' la stessa
             * regola degli inviti, vista dall'altro capo. Uno che nomina un
             * atleta e' invece una notizia — «questa persona e' entrata con un
             * invito che ho visto» — e puo' darla qualunque membro dello staff:
             * senza, un atleta entrato mentre il proprietario e' offline
             * resterebbe invisibile al secondo coach.
             */
            const issuerMayCertify =
              issuer !== undefined &&
              (certificate.role === "member"
                ? issuer.role === "coach" || issuer.role === "owner"
                : isChannelAnchor(issuer.signingPublicKey, profile.channelId));
            const trusted =
              certificate.gymId === profile.gymId &&
              issuerMayCertify &&
              identityIdFromSigningKey(certificate.subjectSigningKey) === certificate.subjectId &&
              // Un certificato per chi e' stato revocato lo rimetterebbe dentro
              // dalla finestra: era esattamente cosi' che la revoca si disfaceva.
              // Ma un certificato emesso *dopo* la revoca e' il rientro
              // legittimo — lo staff ha accolto un invito nuovo — e sui
              // dispositivi che non hanno visto quell'invito questo e' l'unico
              // modo di saperlo.
              !revokedPeers.some(
                (revocation) =>
                  revocation.subjectId === certificate.subjectId &&
                  Date.parse(revocation.revokedAt) >= Date.parse(certificate.issuedAt),
              ) &&
              verifyRoleCertificate(certificate, issuer.signingPublicKey);
            if (trusted) {
              peers = dedupePeers([
                ...peers,
                {
                  id: certificate.subjectId,
                  signingPublicKey: certificate.subjectSigningKey,
                  encryptionPublicKey: certificate.subjectEncryptionKey,
                  role: certificate.role,
                },
              ]);
              // Le revoche superate dal certificato si dimenticano: restando,
              // farebbero scartare ogni messaggio diretto del rientrato.
              revokedPeers = revokedPeers.filter(
                (revocation) =>
                  revocation.subjectId !== certificate.subjectId ||
                  Date.parse(revocation.revokedAt) >= Date.parse(certificate.issuedAt),
              );
              recipientStatus = {
                subjectId: certificate.subjectId,
                active: true,
                changedAt: certificate.issuedAt,
              };
            }
          }

          if (operation.kind === "role.revoked") {
            const revocation = remote(
              () => RoleRevocationSchema.parse(operation.payload),
              "Revoca non valida",
            );
            const currentCertificate = await loadDocument<RoleCertificate>(
              `admin:${profile.gymId}:role:${revocation.subjectId}`,
            );
            const valid =
              revocation.gymId === profile.gymId &&
              revocation.revokedBy === authorizedAuthor.id &&
              (!currentCertificate ||
                Date.parse(currentCertificate.issuedAt) <= Date.parse(revocation.revokedAt)) &&
              verifyRoleRevocation(revocation, authorizedAuthor.signingPublicKey);
            if (valid) {
              peers = peers.filter((peer) => peer.id !== revocation.subjectId);
              // La revoca si conserva, non si esegue e basta: e' cio' che le
              // permette di sopravvivere all'invito del revocato.
              if (!revokedPeers.some((entry) => entry.id === revocation.id)) {
                revokedPeers = [...revokedPeers, revocation];
              }
              if (revocation.subjectId === identity.id) {
                /**
                 * Il revocato sono io. La revoca e' firmata dal proprietario e
                 * gia' verificata: da qui in poi l'app lo dice, invece di
                 * lasciar prenotare nel vuoto. La propria pianificazione resta:
                 * il calendario rimane consultabile, in sola lettura.
                 */
                selfRevocation = revocation;
              } else {
                // Le prenotazioni del revocato escono da calendario e conteggi
                // di capienza; per gli atleti, che hanno solo la propria, e' un
                // no-op. Se rientra con un invito nuovo, la sua pianificazione
                // riarriva da lui alla prima sincronizzazione.
                await removeSchedule(profile.gymId, revocation.subjectId);
              }
              recipientStatus = {
                subjectId: revocation.subjectId,
                active: false,
                changedAt: revocation.revokedAt,
              };
            }
          }

          /**
           * «Il tuo invito non valeva piu'». Si accetta solo se riguarda
           * proprio questa identita' e viene da chi accoglie: un rifiuto
           * indirizzato a un altro, o firmato da chi non e' l'autore
           * dell'envelope, non e' una notizia su di noi.
           */
          if (operation.kind === "invite.rejected") {
            const rejection = remote(
              () => InviteRejectionSchema.parse(operation.payload),
              "Rifiuto di invito non valido",
            );
            if (
              rejection.gymId === profile.gymId &&
              rejection.subjectId === identity.id &&
              rejection.rejectedBy === authorizedAuthor.id &&
              authorizedAuthor.role !== "member" &&
              verifyInviteRejection(rejection, authorizedAuthor.signingPublicKey)
            ) {
              rejectedAccess = {
                inviteId: rejection.inviteId,
                reason: rejection.reason,
                rejectedAt: rejection.rejectedAt,
                rejectedBy: rejection.rejectedBy,
              };
            }
          }

          if (operation.kind === "invite.consumed") {
            const consumption = remote(
              () => InviteConsumptionSchema.parse(operation.payload),
              "Consumo di invito non valido",
            );
            const valid =
              consumption.gymId === profile.gymId &&
              consumption.recordedBy === authorizedAuthor.id &&
              verifyInviteConsumption(consumption, authorizedAuthor.signingPublicKey);
            const alreadyKnown = spentInvites.some(
              (entry) => entry.inviteId === consumption.inviteId,
            );
            if (valid && !alreadyKnown) spentInvites = [...spentInvites, consumption];
          }

          /**
           * Il lasciapassare nominativo che lo staff allega. Si accetta solo se
           * regge da solo — catena firmata, ancorata al canale, intestata a noi
           * — e solo se e' quello del canale su cui siamo: chi lo manda non e'
           * per questo qualcuno di cui fidarsi.
           */
          const grant =
            message.channelPass &&
            profile.channelId &&
            JSON.stringify(message.channelPass) !== JSON.stringify(profile.channelProof) &&
            verifyChannelAdmission({
              channelId: profile.channelId,
              clientId: identity.id,
              clientSigningKey: identity.signingPublicKey,
              proof: message.channelPass,
            })
              ? message.channelPass
              : undefined;

          /**
           * Chi accoglie un nuovo arrivato e' anche chi puo' dire che il suo
           * invito e' stato speso. Da qui in poi lo stesso link non fa entrare
           * nessun altro: e' il `nonce` che esisteva e che nessuno consumava.
           */
          const consumedNow =
            !wasKnown && profile.role !== "member" && message.authorization
              ? recordInviteConsumption({
                  recorder: identity,
                  gymId: profile.gymId,
                  inviteId: message.authorization.payload.id,
                  consumedBy: message.author.id,
                })
              : undefined;
          if (consumedNow && !spentInvites.some((entry) => entry.inviteId === consumedNow.inviteId))
            spentInvites = [...spentInvites, consumedNow];

          let nextProfile: LocalProfile = {
            ...profile,
            peers,
            revokedPeers,
            spentInvites,
            ...(rejectedAccess ? { rejectedAccess } : {}),
            ...(grant ? { channelProof: grant } : {}),
            ...(selfRevocation
              ? {
                  revokedAccess: {
                    revokedAt: selfRevocation.revokedAt,
                    revokedBy: selfRevocation.revokedBy,
                  },
                }
              : {}),
          };
          await persistProfile(nextProfile);

          /**
           * Un envelope applicato che viene dallo staff e' la prova che la
           * palestra ci sta ancora parlando. L'orologio del silenzio riparte
           * da qui; lo stato si tocca solo al cambio di minuto, per non far
           * pagare un render a ogni envelope di una raffica.
           */
          if (authorizedAuthor.role !== "member") {
            const contactAt = new Date().toISOString();
            await setMeta("lastStaffContactAt", contactAt);
            setState((previous) =>
              previous.lastStaffContactAt?.slice(0, 16) === contactAt.slice(0, 16)
                ? previous
                : { ...previous, lastStaffContactAt: contactAt },
            );
          }

          /**
           * Un autore mai visto entra con un invito firmato: da quel momento e'
           * un pari legittimo, ma lo sa soltanto chi ha ricevuto quell'envelope.
           * Lo staff ne fa un certificato e lo dice a tutti, altrimenti il
           * secondo coach non cifrerebbe mai nulla per lui.
           */
          if (consumedNow) {
            await createAndQueueDomainChange({
              identity,
              profile: nextProfile,
              kind: "invite.consumed",
              payload: consumedNow,
              recipients: recipientsFor(
                "invite.consumed",
                consumedNow,
                toPublicIdentity(identity),
                nextProfile.peers,
              ),
              relay,
            });
            await ensureCoachPushWakeGrants(identity, nextProfile, relay);
          }
          /**
           * Il titolo di coach lo firma solo chi ha fondato: un certificato
           * emesso da un coach nessuno lo accetterebbe, e annunciare qualcosa
           * che gli altri scartano confonde e basta. L'atleta invece si annuncia
           * da chiunque sia dello staff. Il nuovo arrivato resta comunque fra i
           * *propri* pari, poco sopra: quello che il coach perde e' la facolta'
           * di presentarlo agli altri, non quella di parlargli.
           */
          const mayCertify =
            authorizedAuthor.role === "member" ||
            isChannelAnchor(identity.signingPublicKey, nextProfile.channelId);
          if (!wasKnown && nextProfile.role !== "member" && mayCertify) {
            const certificate = issueRoleCertificate({
              issuer: identity,
              gymId: profile.gymId,
              subject: message.author,
              role: authorizedAuthor.role,
            });
            await saveDocument(`admin:${profile.gymId}:role:${authorizedAuthor.id}`, certificate);
            await syncRecipientStatus(
              identity,
              nextProfile,
              relay,
              certificate.subjectId,
              true,
              certificate.issuedAt,
            );
            const pass = grantFor(identity, nextProfile, message.author);
            await createAndQueueDomainChange({
              identity,
              profile: nextProfile,
              kind: "role.issued",
              payload: certificate,
              recipients: recipientsFor(
                "role.issued",
                certificate,
                toPublicIdentity(identity),
                nextProfile.peers,
              ),
              // Entrato con un invito, resta con un lasciapassare suo: quello
              // dell'invito scade con l'invito, e senza questo l'atleta
              // perderebbe il canale nel giro di una settimana.
              ...(pass ? { channelPass: pass } : {}),
              relay,
            });
          }

          if (operation.kind === "push.wake.granted") {
            const wakeGrant = remote(
              () => PushWakeGrantSchema.parse(operation.payload),
              "Permesso notifiche non valido",
            );
            if (
              wakeGrant.subjectId !== identity.id ||
              wakeGrant.channelId !== profile.channelId ||
              !verifyPushWakeGrant(wakeGrant)
            ) {
              reject("Permesso notifiche non valido");
            }
            nextProfile = { ...nextProfile, pushWakeGrant: wakeGrant };
            await persistProfile(nextProfile);
          }

          if (operation.kind === "broadcast.published") {
            const incoming = remote(
              () => BroadcastMessageSchema.parse(operation.payload),
              "Broadcast non valido",
            );
            if (incoming.gymId !== profile.gymId || incoming.createdBy !== operation.authorId) {
              reject("Broadcast di un'altra palestra");
            }
            await persistBroadcast(incoming, envelope.id, false, new Date().toISOString());
            if (nextProfile.role === "member") {
              const receipt: BroadcastReceipt = BroadcastReceiptSchema.parse({
                schemaVersion: 1,
                id: createId("receipt"),
                gymId: profile.gymId,
                broadcastId: incoming.id,
                memberId: identity.id,
                state: "received",
                occurredAt: new Date().toISOString(),
              });
              const receiptEnvelope = await createAndQueueDomainChange({
                identity,
                profile: nextProfile,
                kind: "broadcast.receipt",
                payload: receipt,
                recipients: recipientsFor(
                  "broadcast.receipt",
                  receipt,
                  toPublicIdentity(identity),
                  nextProfile.peers,
                ),
                deferQueue: true,
              });
              const projection = await saveBroadcastReceiptOutbox(receipt, {
                envelope: receiptEnvelope,
                attempts: 0,
                nextAttemptAt: new Date().toISOString(),
              });
              await persistBroadcastReceipt(receipt, projection);
              await relay.flushQueued();
            }
          }

          if (operation.kind === "broadcast.manifest" && nextProfile.role !== "member") {
            const manifest = remote(
              () => BroadcastManifestSchema.parse(operation.payload),
              "Manifest broadcast non valido",
            );
            const uniqueRecipients = new Set(manifest.recipients.map((entry) => entry.memberId));
            if (
              manifest.gymId !== profile.gymId ||
              manifest.senderId !== operation.authorId ||
              uniqueRecipients.size !== manifest.recipients.length
            ) {
              reject("Manifest broadcast non valido");
            }
            await persistBroadcastManifest(manifest);
          }

          if (operation.kind === "broadcast.receipt") {
            const receipt = remote(
              () => BroadcastReceiptSchema.parse(operation.payload),
              "Ricevuta broadcast non valida",
            );
            if (receipt.gymId !== profile.gymId || receipt.memberId !== operation.authorId) {
              reject("Ricevuta broadcast non valida");
            }
            await persistBroadcastReceipt(receipt);
          }

          if (operation.kind === "gym.updated") {
            const incoming = normalizeGym(
              remote(() => GymSchema.parse(operation.payload), "Palestra remota non valida"),
            );
            const awaiting = (await getMeta<boolean>("awaitingInitialSync")) ?? false;
            const stored = await loadGymDocuments(profile.gymId);
            const local = stored ? normalizeGym(GymSchema.parse(stored)) : undefined;
            // Finche' si aspetta il primo stato reale il documento locale e' un
            // segnaposto costruito dall'invito: fonderlo farebbe vincere un fuso
            // orario inventato sul fuso vero della palestra.
            const merged = awaiting || !local ? incoming : mergeGym(local, incoming);
            await persistGym(merged);
            if (awaiting) {
              await setMeta("awaitingInitialSync", false);
              setState((previous) => ({ ...previous, awaitingInitialSync: false }));
            }
            // Il mittente non sapeva quello che sapevamo noi: gli si restituisce
            // l'unione, una volta sola, perche' il merge e' idempotente e al giro
            // successivo non ci sara' piu' niente da dire.
            if (!sameDocument(merged, incoming)) {
              await createAndQueueDomainChange({
                identity,
                profile: nextProfile,
                kind: "gym.updated",
                payload: merged,
                recipients: recipientsFor(
                  "gym.updated",
                  merged,
                  toPublicIdentity(identity),
                  nextProfile.peers,
                ),
                relay,
              });
            }
          }

          if (operation.kind === "schedule.updated") {
            const incoming = normalizeMemberSchedule(
              remote(
                () => MemberScheduleSchema.parse(operation.payload),
                "Pianificazione remota non valida",
              ),
            );
            const mayRead = nextProfile.role !== "member" || incoming.memberId === identity.id;
            const retired = nextProfile.revokedPeers?.some(
              (entry) => entry.subjectId === incoming.memberId,
            );
            if (mayRead && !retired) {
              const stored = await loadMemberScheduleDocument(profile.gymId, incoming.memberId);
              const local = stored
                ? normalizeMemberSchedule(MemberScheduleSchema.parse(stored))
                : undefined;
              const merged = local ? mergeMemberSchedule(local, incoming) : incoming;
              await persistSchedule(merged);
              if (!sameDocument(merged, incoming)) {
                await createAndQueueDomainChange({
                  identity,
                  profile: nextProfile,
                  kind: "schedule.updated",
                  payload: merged,
                  recipients: recipientsFor(
                    "schedule.updated",
                    merged,
                    toPublicIdentity(identity),
                    nextProfile.peers,
                  ),
                  relay,
                });
              }
            }
          }

          /**
           * Il digest serve a chi non ha le pianificazioni: lo staff le ha, e
           * conserva i propri conteggi veri. Se lo staff lo archiviasse, ogni
           * copia ricevuta rimetterebbe in discussione la propria e i coach si
           * rimanderebbero digest a vicenda senza fine.
           */
          if (operation.kind === "occupancy.published" && nextProfile.role === "member") {
            const incoming = normalizeOccupancyDigest(
              remote(
                () => OccupancyDigestSchema.parse(operation.payload),
                "Capienza remota non valida",
              ),
            );
            if (incoming.gymId !== profile.gymId) reject("Capienza di un'altra palestra");
            const stored = await loadOccupancyDocument(profile.gymId);
            const local = stored ? OccupancyDigestSchema.parse(stored) : undefined;
            await persistOccupancy(local ? mergeOccupancyDigest(local, incoming) : incoming);
          }

          if (operation.kind === "sync.requested" && nextProfile.role !== "member") {
            const request = remote(
              () => SyncRequestSchema.parse(operation.payload),
              "Richiesta di allineamento non valida",
            );
            // La propria richiesta non si esaudisce da se': arriva anche a questo
            // dispositivo perche' l'identita' e' la stessa.
            const ownRequest =
              request.requesterId === identity.id &&
              (!request.deviceId || request.deviceId === deviceIdRef.current);
            if (ownRequest) return;
            const stored = await loadGymDocuments(profile.gymId);
            if (!stored) return;
            const gym = normalizeGym(GymSchema.parse(stored));
            const scheduleIds = (await getMeta<string[]>("scheduleIds")) ?? [];
            const schedules = (
              await Promise.all(
                scheduleIds.map((id) => loadMemberScheduleDocument(profile.gymId, id)),
              )
            )
              .filter((document): document is NonNullable<typeof document> => Boolean(document))
              .map((document) => normalizeMemberSchedule(MemberScheduleSchema.parse(document)));
            // Il ruolo del richiedente decide l'ampiezza: un coach ripristinato
            // senza le schedule altrui si ritroverebbe una palestra cieca.
            const recovery = selectRecoveryState(
              gym,
              schedules,
              request.requesterId,
              authorizedAuthor.role,
            );
            const hasDocument = (key: string, value: unknown) =>
              request.knownDocuments?.[key] === documentFingerprint(value);
            const pass = grantFor(identity, nextProfile, message.author);
            // Le proiezioni di device diversi non si scavalcano sul relay.
            const sourceDeviceId = deviceIdRef.current ?? (await ensureDeviceId());
            const replacementKey = (document: string) =>
              opaqueChannelId(
                canonicalJson([
                  "recovery",
                  profile.channelId,
                  identity.id,
                  sourceDeviceId,
                  authorizedAuthor.id,
                  document,
                ]),
              );
            if (request.needsChannelPass || !hasDocument("gym", recovery.gym))
              await createAndQueueDomainChange({
                identity,
                profile: nextProfile,
                kind: "gym.updated",
                payload: recovery.gym,
                replacementKey: replacementKey("gym"),
                recipients: [authorizedAuthor],
                // Ogni riallineamento e' anche il momento in cui si rinnova il
                // titolo di chi lo chiede: chi e' rientrato da un backup vecchio,
                // o con un lasciapassare ormai scaduto, qui ne riceve uno buono.
                ...(pass ? { channelPass: pass } : {}),
                relay,
              });
            for (const schedule of recovery.schedules) {
              if (hasDocument(`schedule:${schedule.memberId}`, schedule)) continue;
              await createAndQueueDomainChange({
                identity,
                profile: nextProfile,
                kind: "schedule.updated",
                payload: schedule,
                replacementKey: replacementKey(`schedule:${schedule.memberId}`),
                recipients: [authorizedAuthor],
                relay,
              });
            }
            if (authorizedAuthor.role === "member") {
              const fromDate = todayInZone(recovery.gym.timeZone);
              const occupancy = buildOccupancyDigest({
                gym: recovery.gym,
                schedules,
                fromDate,
                days: OCCUPANCY_WINDOW_DAYS,
                generatedBy: identity.id,
              });
              if (
                request.knownDocuments?.occupancy !==
                occupancyInventoryFingerprint(occupancy, fromDate)
              )
                await createAndQueueDomainChange({
                  identity,
                  profile: nextProfile,
                  kind: "occupancy.published",
                  replacementKey: replacementKey("occupancy"),
                  payload: occupancy,
                  recipients: [authorizedAuthor],
                  relay,
                });
            }
            /**
             * A un coach che rientra serve anche sapere chi c'e': senza il roster
             * cifrerebbe le proprie modifiche solo per chi gli ha risposto.
             */
            if (authorizedAuthor.role !== "member") {
              if (nextProfile.role === "owner") {
                for (const revocation of nextProfile.revokedPeers ?? []) {
                  if (hasDocument(`revocation:${revocation.subjectId}`, revocation)) continue;
                  await createAndQueueDomainChange({
                    identity,
                    profile: nextProfile,
                    kind: "role.revoked",
                    payload: revocation,
                    replacementKey: replacementKey(`role:${revocation.subjectId}`),
                    recipients: [authorizedAuthor],
                    relay,
                  });
                }
              }
              for (const peer of nextProfile.peers) {
                if (peer.id === authorizedAuthor.id || peer.id === identity.id) continue;
                const certificate = await loadDocument<RoleCertificate>(
                  `admin:${profile.gymId}:role:${peer.id}`,
                );
                if (!certificate) continue;
                if (hasDocument(`role:${peer.id}`, certificate)) continue;
                await createAndQueueDomainChange({
                  identity,
                  profile: nextProfile,
                  kind: "role.issued",
                  replacementKey: replacementKey(`role:${peer.id}`),
                  payload: RoleCertificateSchema.parse(certificate),
                  recipients: [authorizedAuthor],
                  relay,
                });
              }
            }
          }
          if (recipientStatus)
            await syncRecipientStatus(
              identity,
              nextProfile,
              relay,
              recipientStatus.subjectId,
              recipientStatus.active,
              recipientStatus.changedAt,
            );
          channel?.postMessage("remote");
        });
      },
    });
    relayRef.current = relay;
    // React StrictMode mounts, cleans up and mounts effects again in development.
    // Deferring the first connection avoids opening and immediately closing a
    // WebSocket during that probe.
    const connectTimer = window.setTimeout(() => relay.connect(), 0);
    const online = () => relay.ensureAlive();
    globalThis.addEventListener("online", online);
    // Il ritorno in primo piano e' il momento in cui un socket sopravvissuto allo
    // standby si rivela muto: "online" non scatta, perche' la rete non e' mai
    // cambiata dal punto di vista del browser.
    // `visibilitychange` scatta anche all'uscita: verificare un canale che nessuno
    // sta guardando non serve.
    const wake = () => {
      if (document.visibilityState !== "hidden") relay.ensureAlive();
    };
    const wakeEvents = ["visibilitychange", "focus", "pageshow"];
    for (const event of wakeEvents) globalThis.addEventListener(event, wake);
    return () => {
      window.clearTimeout(connectTimer);
      globalThis.removeEventListener("online", online);
      for (const event of wakeEvents) globalThis.removeEventListener(event, wake);
      relay.close();
    };
  }, [
    deviceId,
    enqueueApply,
    persistGym,
    persistBroadcast,
    persistBroadcastManifest,
    persistBroadcastReceipt,
    persistOccupancy,
    persistProfile,
    persistSchedule,
    relayChannelId,
    relayHasProfile,
    relayIdentity,
    relayProofKey,
    removeSchedule,
    requestAlignment,
    uploadBackup,
  ]);

  useEffect(() => {
    if (!state.ready || !state.gym || !state.profile || state.profile.role === "member") return;
    let active = true;
    void (async () => {
      /**
       * Fine raffica, non per elemento: il relay in riconnessione consegna
       * decine di envelope e ciascuno tocca `state.schedules`. La coda li
       * applica in serie; attenderla fa sì che le esecuzioni intermedie di
       * questo effect trovino `active` già spento e si ritirino, e che la
       * proiezione venga ricalcolata una volta sola, in coda (PERF-05).
       */
      await applyQueueRef.current;
      if (!active) return;
      const previous = await loadCoachProjectionDocument(state.gym!.id);
      if (!active) return;
      const projections = projectAttendance(
        state.gym!,
        state.schedules,
        new Date().toISOString(),
        7,
      );
      const generated = deriveAlerts(previous ? [...previous.projections] : [], projections);
      const alerts = [...(previous ? previous.alerts : []), ...generated].filter(
        (alert, index, all) => all.findIndex((candidate) => candidate.id === alert.id) === index,
      );
      await saveCoachProjectionDocument({
        schemaVersion: 1,
        gymId: state.gym!.id,
        projections,
        alerts,
        generatedAt: new Date().toISOString(),
      });
      if (active) setState((current) => ({ ...current, alerts }));
    })().catch((error) => {
      if (active) setState((current) => ({ ...current, error: errorMessage(error) }));
    });
    return () => {
      active = false;
    };
  }, [state.gym, state.profile, state.ready, state.schedules]);

  /**
   * Lo staff pubblica la capienza perche' l'atleta possa decidere se spostarsi.
   * Parte solo quando i numeri cambiano: il digest porta anche l'istante in cui
   * e' stato composto, e confrontare quello significherebbe pubblicare a ogni
   * ricalcolo, cioe' a ogni render.
   */
  useEffect(() => {
    const gym = state.gym;
    const profile = state.profile;
    if (!state.ready || !gym || !profile || profile.role === "member") return;
    if (!profile.peers.some((peer) => peer.role === "member")) return;
    const schedules = state.schedules;
    if (occupancyTimerRef.current) window.clearTimeout(occupancyTimerRef.current);
    occupancyTimerRef.current = window.setTimeout(() => {
      occupancyTimerRef.current = undefined;
      void (async () => {
        const identity = stateRef.current.identity;
        if (!identity) return;
        const digest = buildOccupancyDigest({
          gym,
          schedules,
          fromDate: todayInZone(gym.timeZone),
          days: OCCUPANCY_WINDOW_DAYS,
          generatedBy: identity.id,
        });
        const fingerprint = occupancyFingerprint(digest);
        if ((await getMeta<string>("occupancyFingerprint")) === fingerprint) return;
        await setMeta("occupancyFingerprint", fingerprint);
        await queueDomainChange("occupancy.published", digest);
      })().catch(() => undefined);
    }, OCCUPANCY_DEBOUNCE_MS);
    return () => {
      if (occupancyTimerRef.current) window.clearTimeout(occupancyTimerRef.current);
    };
  }, [queueDomainChange, state.gym, state.profile, state.ready, state.schedules]);

  /**
   * Chi guida la palestra ha un nome come tutti gli altri, e agli altri arriva
   * dalla pianificazione: i profili di staff nati prima di questa regola non ne
   * avevano una, e comparivano nell'elenco «Chi ha accesso» dei colleghi come
   * «In attesa del primo collegamento». Si ripara da se': quando la
   * pianificazione c'e', questa condizione e' falsa e non torna piu' vera, senza
   * bisogno di ricordare che e' gia' stato fatto.
   */
  useEffect(() => {
    const identity = state.identity;
    const profile = state.profile;
    if (!state.ready || !identity || !profile || !state.gym) return;
    if (profile.role === "member" || state.awaitingInitialSync) return;
    if (state.schedules.some((schedule) => schedule.memberId === identity.id)) return;
    let active = true;
    void (async () => {
      // Stessa attesa della proiezione: durante una raffica di envelope la
      // pianificazione vera puo' essere in arrivo proprio adesso.
      await applyQueueRef.current;
      if (!active) return;
      /**
       * Il disco, non solo lo stato: una pianificazione vuota che vincesse su
       * quella vera partirebbe come `schedule.updated` e cancellerebbe gli
       * allenamenti anche in palestra.
       */
      const stored = await loadMemberScheduleDocument(profile.gymId, identity.id);
      if (stored || !active) return;
      const schedule = MemberScheduleSchema.parse({
        schemaVersion: 1,
        gymId: profile.gymId,
        memberId: identity.id,
        displayName: profile.displayName,
        recurringSlotIds: [],
        recurring: [],
        exceptions: [],
        conflicts: [],
        updatedAt: new Date().toISOString(),
      });
      await persistSchedule(schedule);
      await queueDomainChange("schedule.updated", schedule);
    })().catch(() => undefined);
    return () => {
      active = false;
    };
  }, [
    persistSchedule,
    queueDomainChange,
    state.awaitingInitialSync,
    state.gym,
    state.identity,
    state.profile,
    state.ready,
    state.schedules,
  ]);

  const createFounder = useCallback(
    async (displayName: string, gymName: string) => {
      const identity = createIdentity();
      const now = new Date().toISOString();
      const gym: Gym = GymSchema.parse({
        schemaVersion: 1,
        id: createId("gym"),
        name: gymName,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Rome",
        slotDurationMinutes: 120,
        slots: defaultSlots(),
        exceptions: [],
        updatedAt: now,
        updatedBy: identity.id,
      });
      const profile: LocalProfile = {
        displayName,
        role: "owner",
        gymId: gym.id,
        // Il canale della palestra nasce dalla chiave di chi la fonda: e' cio'
        // che permette al relay di riconoscere chi ha titolo per starci senza
        // sapere nulla della palestra. Il fondatore non ha lasciapassare da
        // esibire — e' lui l'ancora.
        channelId: channelIdForOwner(identity.signingPublicKey),
        peers: [{ ...toPublicIdentity(identity), role: "owner" }],
      };
      /**
       * Anche chi fonda la palestra ha un nome, e agli altri arriva per la
       * stessa strada di tutti: la propria pianificazione. Senza, il fondatore
       * compariva nell'elenco «Chi ha accesso» dei suoi coach come «In attesa
       * del primo collegamento» — di se stesso, per sempre. Nasce vuota: non e'
       * una dichiarazione di allenamenti, e' la sola cosa che porta il nome.
       */
      const schedule: MemberSchedule = MemberScheduleSchema.parse({
        schemaVersion: 1,
        gymId: gym.id,
        memberId: identity.id,
        displayName,
        recurringSlotIds: [],
        recurring: [],
        exceptions: [],
        conflicts: [],
        updatedAt: now,
      });
      await saveIdentity(identity);
      await saveGymDocuments(gym);
      await saveMemberScheduleDocument(schedule);
      await saveDocument(
        `admin:${gym.id}:role:${identity.id}`,
        issueRoleCertificate({
          issuer: identity,
          gymId: gym.id,
          subject: toPublicIdentity(identity),
          role: "owner",
        }),
      );
      await setMeta("profile", profile);
      await setMeta("scheduleIds", [identity.id]);
      setState({
        ready: true,
        identity,
        backupConfirmed: false,
        profile,
        gym,
        schedules: [schedule],
        alerts: [],
        broadcasts: [],
        broadcastManifests: [],
        broadcastReceipts: [],
        notificationPermission:
          typeof Notification === "undefined" ? "unsupported" : Notification.permission,
        pushSubscribed: false,
        invites: [],
        awaitingInitialSync: false,
        sync: emptySync,
      });
      channel?.postMessage("founder");
      scheduleBackupUpload();
    },
    [scheduleBackupUpload],
  );

  const acceptInvite = useCallback(
    async (displayName: string, invite: SignedInvite) => {
      /**
       * Il rifiuto detto per quello che e'. Un invito che non viene da chi ha
       * fondato la palestra non fara' entrare da nessuna parte — lo scarta il
       * core, e lo scarterebbe comunque ogni dispositivo dall'altra parte — ma
       * «invito non valido o scaduto» manderebbe a cercare la ragione sbagliata.
       */
      if (!isChannelAnchor(invite.issuerSigningKey, invite.payload.channelId)) {
        throw new Error(
          "Questo invito non è stato creato dal proprietario della palestra: chiedigli un link nuovo",
        );
      }
      if (!verifyInvite(invite)) throw new Error("Invito non valido o scaduto");
      const existingIdentity = await loadIdentity();
      const identity = existingIdentity?.identity ?? createIdentity();
      // Chi invita e' il fondatore, e la sua chiave riproduce il canale: e' il
      // controllo qui sopra. Va registrato come proprietario, altrimenti la sua
      // `role.revoked` — che solo un owner puo' firmare — verrebbe scartata
      // proprio dal dispositivo che dovrebbe mostrarla.
      const owner: AuthorizedPeer = {
        id: invite.payload.issuedBy,
        signingPublicKey: invite.issuerSigningKey,
        encryptionPublicKey: invite.issuerEncryptionKey,
        role: "owner",
      };
      const profile: LocalProfile = {
        displayName,
        role: invite.payload.role,
        gymId: invite.payload.gymId,
        channelId: invite.payload.channelId,
        // Il lasciapassare al portatore dell'invito: vale quanto l'invito, e
        // basta ad arrivare al primo contatto con lo staff, che ne consegna
        // uno intestato.
        channelProof: invite.channelProof,
        peers: [owner, { ...toPublicIdentity(identity), role: invite.payload.role }],
      };
      const placeholder: Gym = {
        schemaVersion: 1,
        id: invite.payload.gymId,
        name: invite.payload.gymName,
        timeZone: "Europe/Rome",
        slotDurationMinutes: 120,
        slots: [],
        retiredSlots: [],
        exceptions: [],
        updatedAt: new Date().toISOString(),
        updatedBy: invite.payload.issuedBy,
      };
      /**
       * Chi si ricollega alla stessa palestra con un invito nuovo — succede a
       * ogni rientro dopo una revoca, e ogni volta che il coach rifà un link —
       * riusa l'identita' che ha gia'. La sua pianificazione va con lei: una
       * pianificazione vuota qui non restava un dettaglio locale, partiva come
       * `schedule.updated` e cancellava gli allenamenti anche in palestra.
       */
      const previousSchedule = existingIdentity
        ? await loadMemberScheduleDocument(invite.payload.gymId, identity.id)
        : undefined;
      const schedule: MemberSchedule = previousSchedule
        ? {
            ...normalizeMemberSchedule(MemberScheduleSchema.parse(previousSchedule)),
            displayName,
            updatedAt: new Date().toISOString(),
          }
        : {
            schemaVersion: 1,
            gymId: profile.gymId,
            memberId: identity.id,
            displayName,
            recurringSlotIds: [],
            recurring: [],
            exceptions: [],
            conflicts: [],
            updatedAt: new Date().toISOString(),
          };
      await saveIdentity(identity, existingIdentity?.backupConfirmed ?? false);
      await setMeta("profile", profile);
      await setMeta("acceptedInvite", invite);
      await setMeta("scheduleIds", [identity.id]);
      await setMeta("awaitingInitialSync", true);
      // L'orologio del silenzio dello staff parte dall'ingresso: prima di
      // adesso non c'era nessuno che dovesse farsi sentire.
      const enteredAt = new Date().toISOString();
      await setMeta("lastStaffContactAt", enteredAt);
      await saveGymDocuments(placeholder);
      await saveMemberScheduleDocument(schedule);
      await createAndQueueDomainChange({
        identity,
        profile,
        kind: "sync.requested",
        payload: { requesterId: identity.id, requestedAt: new Date().toISOString() },
        recipients: [owner],
        dedupeKey: ALIGNMENT_DEDUPE_KEY,
        authorization: invite,
      });
      // La pianificazione appena nata porta il nome: senza questo invio il
      // coach conoscerebbe l'atleta solo alla prima prenotazione, e fino ad
      // allora lo vedrebbe come "in attesa del primo collegamento".
      await createAndQueueDomainChange({
        identity,
        profile,
        kind: "schedule.updated",
        payload: schedule,
        recipients: [owner],
        authorization: invite,
      });
      const currentUrl = new URL(location.href);
      currentUrl.searchParams.delete("invite");
      history.replaceState({}, "", `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`);
      setState({
        ready: true,
        identity,
        backupConfirmed: existingIdentity?.backupConfirmed ?? false,
        profile,
        gym: placeholder,
        schedules: [schedule],
        alerts: [],
        broadcasts: [],
        broadcastManifests: [],
        broadcastReceipts: [],
        notificationPermission:
          typeof Notification === "undefined" ? "unsupported" : Notification.permission,
        pushSubscribed: false,
        invites: [],
        awaitingInitialSync: true,
        acceptedInviteId: invite.payload.id,
        lastStaffContactAt: enteredAt,
        sync: { state: navigator.onLine ? "connecting" : "offline", pending: 2 },
      });
      scheduleBackupUpload();
    },
    [scheduleBackupUpload],
  );

  const restoreIdentity = useCallback(async (mnemonic: string) => {
    const identity = identityFromMnemonic(mnemonic);
    await saveIdentity(identity, true);
    setState((previous) => ({ ...previous, identity, backupConfirmed: true }));
    channel?.postMessage("identity");
  }, []);

  /**
   * Le 12 parole da sole devono bastare. L'identita' viene comunque salvata:
   * anche quando il backup non c'e', il rientro con invito riparte da li'.
   */
  const restoreProfile = useCallback(async (mnemonic: string): Promise<RestoreOutcome> => {
    const identity = identityFromMnemonic(mnemonic);
    await saveIdentity(identity, true);
    setState((previous) => ({ ...previous, identity, backupConfirmed: true }));
    channel?.postMessage("identity");

    const relayUrl = resolveRelayUrl({
      runtimeValue: readRuntimeConfig(),
      location,
      development: import.meta.env.DEV,
      developmentUrl: import.meta.env.VITE_RELAY_URL as string | undefined,
    });
    if (!relayUrl || typeof WebSocket === "undefined") {
      return { kind: "identity-only", reason: "relay-unreachable" };
    }

    let blob: string | null;
    try {
      blob = await fetchRemoteBackup(identity, relayUrl);
    } catch {
      // Non aver potuto chiedere e' diverso dall'aver saputo che non c'e'.
      return { kind: "identity-only", reason: "relay-unreachable" };
    }
    if (!blob) return { kind: "identity-only", reason: "no-backup" };

    let backup: ProfileBackup;
    try {
      backup = ProfileBackupSchema.parse(decryptProfileBackup(identity, blob));
    } catch {
      // Un blob manomesso o di uno schema che non riconosciamo vale come assente.
      return { kind: "identity-only", reason: "unreadable" };
    }

    await setMeta("profile", backup.profile);
    if (backup.acceptedInvite) await setMeta("acceptedInvite", backup.acceptedInvite);
    if (backup.gym) await saveGymDocuments(normalizeGym(backup.gym));
    const restoredSchedules = [
      ...(backup.ownSchedule ? [backup.ownSchedule] : []),
      ...(backup.schedules ?? []),
    ]
      .map((schedule) => normalizeMemberSchedule(schedule))
      .filter(
        (schedule, index, all) =>
          all.findIndex((candidate) => candidate.memberId === schedule.memberId) === index,
      );
    for (const schedule of restoredSchedules) await saveMemberScheduleDocument(schedule);
    await setMeta(
      "scheduleIds",
      restoredSchedules.map((schedule) => schedule.memberId),
    );
    if (backup.occupancy) await saveOccupancyDocument(backup.occupancy);
    // Gli inviti fatti prima di perdere il dispositivo: senza, l'elenco
    // ripartirebbe vuoto mentre i link restano vivi la' fuori.
    const restoredInvites = liveInvites(
      backup.issuedInvites ?? [],
      backup.profile.spentInvites ?? [],
    );
    await setMeta("issuedInvites", restoredInvites);
    await setMeta("awaitingInitialSync", true);
    // Come all'ingresso: il silenzio si misura da questo ripristino in poi.
    const restoredAt = new Date().toISOString();
    await setMeta("lastStaffContactAt", restoredAt);

    // Lo snapshot rende l'app usabile subito; il riallineamento fine arriva dal
    // primo staff che torna online.
    const staff = backup.profile.peers.filter(
      (peer) => peer.role !== "member" && peer.id !== identity.id,
    );
    if (staff.length > 0) {
      await createAndQueueDomainChange({
        identity,
        profile: backup.profile,
        kind: "sync.requested",
        payload: { requesterId: identity.id, requestedAt: new Date().toISOString() },
        recipients: staff,
        dedupeKey: ALIGNMENT_DEDUPE_KEY,
        ...(backup.acceptedInvite ? { authorization: backup.acceptedInvite } : {}),
      });
    }

    setState({
      ready: true,
      identity,
      backupConfirmed: true,
      profile: backup.profile,
      ...(backup.gym ? { gym: normalizeGym(backup.gym) } : {}),
      schedules: restoredSchedules,
      ...(backup.occupancy ? { occupancy: backup.occupancy } : {}),
      alerts: [],
      broadcasts: [],
      broadcastManifests: [],
      broadcastReceipts: [],
      notificationPermission:
        typeof Notification === "undefined" ? "unsupported" : Notification.permission,
      pushSubscribed: false,
      invites: restoredInvites,
      awaitingInitialSync: true,
      ...(backup.acceptedInvite ? { acceptedInviteId: backup.acceptedInvite.payload.id } : {}),
      lastStaffContactAt: restoredAt,
      sync: {
        state: navigator.onLine ? "connecting" : "offline",
        pending: staff.length > 0 ? 1 : 0,
      },
    });
    channel?.postMessage("restored");
    return { kind: "profile" };
  }, []);

  const confirmBackup = useCallback(async () => {
    await confirmIdentityBackup();
    setState((previous) => ({ ...previous, backupConfirmed: true }));
    scheduleBackupUpload();
  }, [scheduleBackupUpload]);

  /**
   * La bozza del coach viene confrontata con lo stato di partenza: solo cio' che
   * e' cambiato prende la data di adesso. Salvare l'intera bozza come "nuova"
   * farebbe vincere questa copia su ogni modifica fatta altrove nel frattempo.
   */
  const updateGym = useCallback(
    async (draft: Gym, base?: Gym) => {
      const current = stateRef.current;
      assertNotRevoked(current.profile);
      const authorId = current.identity?.id ?? draft.updatedBy;
      // La bozza si confronta con lo stato da cui e' partita, non con quello di
      // adesso: fra l'apertura del modulo e il salvataggio puo' essere arrivato
      // un orario dall'altro dispositivo, e non e' stato l'utente a toglierlo.
      const reference = base ?? current.gym;
      const committed = reference
        ? commitGymDraft(reference, GymSchema.parse(draft), new Date().toISOString(), authorId)
        : normalizeGym(GymSchema.parse(draft));
      // Il merge con lo stato corrente restituisce cio' che e' arrivato nel
      // frattempo: la bozza porta le proprie decisioni, non l'intera verita'.
      const merged = current.gym ? mergeGym(current.gym, committed) : committed;
      const saved = await persistGym(merged);
      await queueDomainChange("gym.updated", saved);
      channel?.postMessage("gym");
      return saved;
    },
    [persistGym, queueDomainChange],
  );

  const updateSchedule = useCallback(
    async (schedule: MemberSchedule) => {
      // Vale anche per `book` e `cancel`, che passano di qui: una prenotazione
      // accodata dopo la revoca sembrerebbe confermata e non arriverebbe mai.
      assertNotRevoked(stateRef.current.profile);
      await persistSchedule(schedule);
      await queueDomainChange("schedule.updated", schedule);
    },
    [persistSchedule, queueDomainChange],
  );

  /**
   * «Esserci quel giorno a quell'ora». Il vincolo di un solo slot al giorno vive
   * qui dentro, non nella schermata: se la giornata era occupata questa scrittura
   * e' uno spostamento, e lo e' da qualunque parte arrivi la richiesta.
   */
  const book = useCallback(
    async (date: string, slotId: string) => {
      const current = state.schedules.find((schedule) => schedule.memberId === state.identity?.id);
      if (!current) throw new Error("Pianificazione personale non trovata");
      if (!state.gym) throw new Error("Palestra non disponibile");
      await updateSchedule(applyDayBooking(state.gym, current, date, slotId));
    },
    [state.gym, state.identity?.id, state.schedules, updateSchedule],
  );

  const cancel = useCallback(
    async (date: string, sourceSlotId: string) => {
      const current = state.schedules.find((schedule) => schedule.memberId === state.identity?.id);
      if (!current) throw new Error("Pianificazione personale non trovata");
      await updateSchedule(cancelOccurrence(current, date, sourceSlotId));
    },
    [state.identity?.id, state.schedules, updateSchedule],
  );

  const makeInvite = useCallback(
    async (role: "member" | "coach") => {
      if (!state.identity || !state.gym || state.profile?.role !== "owner")
        throw new Error("Solo il proprietario della palestra può creare inviti");
      assertNotRevoked(stateRef.current.profile);
      const channelId = state.profile.channelId;
      if (!channelId) throw new Error("Profilo senza canale: non può invitare");
      /**
       * Il ruolo scritto nel profilo dice cosa questo dispositivo crede di
       * essere; la chiave dice cosa e'. Il canale della palestra e' derivato da
       * quella del fondatore, quindi un invito firmato da chiunque altro non
       * verrebbe accettato da nessuno: meglio non produrlo affatto che
       * consegnare un link che fallisce in mano a chi lo riceve.
       */
      if (!isChannelAnchor(state.identity.signingPublicKey, channelId)) {
        throw new Error("Questo dispositivo non è quello di chi ha fondato la palestra");
      }
      const issuedAt = new Date();
      const expiresAt = new Date(issuedAt.getTime() + INVITE_LIFETIME_MS[role]).toISOString();
      const relay = relayRef.current;
      if (!relay) {
        throw new Error("Il link si crea quando la sincronizzazione è attiva: riprova tra poco");
      }
      const invite = createInvite({
        issuer: state.identity,
        gymId: state.gym.id,
        gymName: state.gym.name,
        channelId,
        role,
        expiresAt,
      });
      /**
       * L'invito non viaggia piu' nell'URL: compresso e cifrato, va in una
       * cassetta del relay, e nell'URL restano il token e — nel frammento, che
       * nessuna richiesta HTTP trasporta — la chiave. La conferma del relay si
       * attende prima di mostrare il link.
       */
      const sealed = await sealInviteLink(invite);
      try {
        await relay.putInviteLink({ token: sealed.token, blob: sealed.blob, expiresAt });
      } catch (error) {
        throw error instanceof Error && error.message !== "Canale relay non autenticato"
          ? error
          : new Error("Il link si crea quando la sincronizzazione è attiva: riprova tra poco");
      }
      /**
       * L'invito si annota: senza saperlo esiste non c'e' modo di mostrarlo in
       * Profilo, e senza mostrarlo non c'e' modo di ritirarlo. L'URL — chiave
       * compresa — resta mostrabile, e viaggia solo nel backup cifrato.
       */
      const issued: IssuedInvite = {
        id: invite.payload.id,
        role,
        issuedAt: issuedAt.toISOString(),
        expiresAt,
        token: sealed.token,
        url: formatInviteLinkUrl(location.origin, sealed),
      };
      const invites = [
        ...liveInvites(stateRef.current.invites, state.profile?.spentInvites ?? []),
        issued,
      ];
      await setMeta("issuedInvites", invites);
      setState((previous) => ({ ...previous, invites }));
      scheduleBackupUpload();
      return issued;
    },
    [
      scheduleBackupUpload,
      state.gym,
      state.identity,
      state.profile?.channelId,
      state.profile?.role,
      state.profile?.spentInvites,
    ],
  );

  const revokeInvite = useCallback(
    async (inviteId: string) => {
      const identity = state.identity;
      const profile = state.profile;
      // Gli inviti sono del proprietario: crearli e ritirarli sono lo stesso
      // comando visto da due lati.
      if (!identity || !profile || profile.role !== "owner") {
        throw new Error("Solo il proprietario della palestra può ritirare un invito");
      }
      // Ritirato senza essere stato speso: `consumedBy` assente e' esattamente
      // questo, e vale per i pari come una consumazione qualunque.
      const consumption = recordInviteConsumption({
        recorder: identity,
        gymId: profile.gymId,
        inviteId,
      });
      /**
       * Il link smette di aprirsi: la voce sul relay viene cancellata, e chi lo
       * apre legge che e' stato ritirato invece di entrare in un percorso che
       * fallira' piu' tardi. Best effort: se il canale non c'e' adesso, la
       * revoca vera — `spentInvites`, qui sotto — parte comunque, e il blob
       * scade da solo con l'invito.
       */
      const withdrawn = stateRef.current.invites.find((entry) => entry.id === inviteId);
      if (withdrawn?.token) relayRef.current?.deleteInviteLink(withdrawn.token);
      const nextProfile: LocalProfile = {
        ...profile,
        spentInvites: [...(profile.spentInvites ?? []), consumption],
      };
      await queueDomainChange("invite.consumed", consumption, undefined, nextProfile);
      await persistProfile(nextProfile);
      const invites = liveInvites(stateRef.current.invites, nextProfile.spentInvites ?? []);
      await setMeta("issuedInvites", invites);
      setState((previous) => ({ ...previous, invites }));
    },
    [persistProfile, queueDomainChange, state.identity, state.profile],
  );

  const revokePeer = useCallback(
    async (peerId: string) => {
      if (!state.identity || !state.profile || state.profile.role !== "owner") {
        // Il vincolo e' nel core (`authorization.ts`), non qui: la schermata
        // nasconde il comando a chi non puo' usarlo, e questo e' il fallback.
        throw new Error("Solo il proprietario può revocare un accesso");
      }
      const peer = state.profile.peers.find((candidate) => candidate.id === peerId);
      if (!peer || peer.role === "owner" || peer.id === state.identity.id) {
        throw new Error("Persona non trovata");
      }
      const revocation = createRoleRevocation({
        owner: state.identity,
        gymId: state.profile.gymId,
        subjectId: peerId,
      });
      if (peer.role === "coach" && state.profile.channelId) {
        const pushRevocation = createPushWakeRevocation({
          owner: state.identity,
          channelId: state.profile.channelId,
          subjectId: peerId,
        });
        const pending =
          (await getMeta<ReturnType<typeof createPushWakeRevocation>[]>(
            "pendingPushWakeRevocations",
          )) ?? [];
        await setMeta("pendingPushWakeRevocations", [...pending, pushRevocation]);
        await setMeta(issuedPushWakeGrantKey(state.profile.channelId, peerId), undefined);
        relayRef.current?.revokePushGrant(pushRevocation);
      }

      /**
       * La revoca resta scritta, non solo eseguita: togliere il pari
       * dall'elenco lo faceva tornare «autore sconosciuto», e da sconosciuto
       * rientrava con l'invito che aveva gia' usato.
       */
      const nextProfile = {
        ...state.profile,
        peers: state.profile.peers.filter((candidate) => candidate.id !== peerId),
        revokedPeers: [...(state.profile.revokedPeers ?? []), revocation],
      };
      await persistProfile(nextProfile);
      if (relayRef.current)
        await syncRecipientStatus(
          state.identity,
          nextProfile,
          relayRef.current,
          peerId,
          false,
          revocation.revokedAt,
        );
      /**
       * Anche le sue prenotazioni se ne vanno, non solo il suo posto fra i
       * pari: lasciarle contava il revocato nella capienza e lo mostrava nel
       * roster degli slot, congelato all'ultimo stato ricevuto. Il ricalcolo
       * delle schedules fa ripartire da solo il digest di capienza corretto.
       */
      await removeSchedule(state.profile.gymId, peerId);
      await queueDomainChange("role.revoked", revocation, undefined, nextProfile);
    },
    [persistProfile, queueDomainChange, removeSchedule, state.identity, state.profile],
  );

  const sendBroadcast = useCallback(
    async (body: string, removedSchedule?: MemberSchedule): Promise<string> => {
      if (removedSchedule) removedSchedule = MemberScheduleSchema.parse(removedSchedule);
      const current = stateRef.current;
      const identity = current.identity;
      const profile = current.profile;
      if (!identity || !profile || !profile.channelId || profile.role === "member") {
        throw new Error("Solo lo staff può inviare un broadcast");
      }
      assertNotRevoked(profile);
      const now = new Date().toISOString();
      const message = BroadcastMessageSchema.parse({
        schemaVersion: 1,
        id: createId("broadcast"),
        gymId: profile.gymId,
        body,
        createdBy: identity.id,
        createdAt: now,
      });
      const authorization = await getMeta<SignedInvite>("acceptedInvite");
      const domain = (operation: SignedOperation): DomainMessage => ({
        operation,
        author: toPublicIdentity(identity),
        ...(authorization ? { authorization } : {}),
      });
      const unique = (items: PublicIdentity[]) =>
        items.filter(
          (peer, index, all) => all.findIndex((candidate) => candidate.id === peer.id) === index,
        );
      const self = toPublicIdentity(identity);
      const members = profile.peers.filter(
        (peer) =>
          peer.role === "member" && (!removedSchedule || peer.id === removedSchedule.memberId),
      );
      const contentRecipients = unique([
        self,
        ...profile.peers.filter((peer) => peer.role !== "member"),
        ...members,
      ]);
      const contentOperation = createSignedOperation({
        identity,
        gymId: profile.gymId,
        role: profile.role,
        kind: "broadcast.published",
        payload: message,
        now,
      });
      const contentEnvelope = encryptEnvelope({
        sender: identity,
        channelId: profile.channelId,
        payload: domain(contentOperation),
        recipients: contentRecipients,
        expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      });
      const manifest = BroadcastManifestSchema.parse({
        schemaVersion: 1,
        broadcastId: message.id,
        gymId: profile.gymId,
        contentEnvelopeId: contentEnvelope.id,
        senderId: identity.id,
        recipients: members.map((member) => {
          const displayName = current.schedules.find(
            (schedule) => schedule.memberId === member.id,
          )?.displayName;
          return {
            memberId: member.id,
            ...(displayName ? { displayNameAtSend: displayName } : {}),
          };
        }),
        createdAt: now,
      });
      const manifestOperation = createSignedOperation({
        identity,
        gymId: profile.gymId,
        role: profile.role,
        kind: "broadcast.manifest",
        payload: manifest,
        now,
      });
      const manifestEnvelope = encryptEnvelope({
        sender: identity,
        channelId: profile.channelId,
        payload: domain(manifestOperation),
        recipients: unique([self, ...profile.peers.filter((peer) => peer.role !== "member")]),
        expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      });
      const mayWake =
        profile.role === "owner" ||
        (profile.pushWakeGrant !== undefined && verifyPushWakeGrant(profile.pushWakeGrant));
      const wake =
        mayWake && members.length > 0
          ? createPushWake({
              identity,
              channelId: profile.channelId,
              envelopeId: contentEnvelope.id,
              recipientIds: members.map((member) => member.id),
              ...(profile.role === "coach" && profile.pushWakeGrant
                ? { grant: profile.pushWakeGrant }
                : {}),
            })
          : undefined;
      const queuedAt = new Date().toISOString();
      const scheduleEnvelope = removedSchedule
        ? await createAndQueueDomainChange({
            identity,
            profile,
            kind: "schedule.updated",
            payload: removedSchedule,
            recipients: recipientsFor("schedule.updated", removedSchedule, self, profile.peers),
            ...(authorization ? { authorization } : {}),
            deferQueue: true,
          })
        : undefined;
      await saveBroadcastOutboxBundle({
        message,
        sourceEnvelopeId: contentEnvelope.id,
        manifest,
        ...(removedSchedule ? { schedule: removedSchedule } : {}),
        items: [
          ...(scheduleEnvelope
            ? [{ envelope: scheduleEnvelope, attempts: 0, nextAttemptAt: queuedAt }]
            : []),
          {
            envelope: contentEnvelope,
            attempts: 0,
            nextAttemptAt: queuedAt,
            ...(wake ? { wake } : {}),
          },
          {
            envelope: manifestEnvelope,
            attempts: 0,
            nextAttemptAt: queuedAt,
          },
        ],
      });
      if (removedSchedule) await persistSchedule(removedSchedule, true);
      await persistBroadcast(message, contentEnvelope.id, true);
      await persistBroadcastManifest(manifest, true);
      await relayRef.current?.flushQueued();
      channel?.postMessage("broadcast");
      return message.id;
    },
    [persistBroadcast, persistBroadcastManifest, persistSchedule],
  );

  const removeMemberBooking = useCallback(
    async (memberId: string, date: string) => {
      const current = stateRef.current;
      if (!current.identity || !current.profile || current.profile.role === "member") {
        throw new Error("Solo il coach può rimuovere le prenotazioni degli atleti");
      }
      if (!current.gym) throw new Error("Palestra non disponibile");
      const peer = current.profile.peers.find((candidate) => candidate.id === memberId);
      if (!peer || peer.role !== "member") throw new Error("Atleta non trovato");
      const schedule = current.schedules.find((candidate) => candidate.memberId === memberId);
      if (!schedule) throw new Error("Pianificazione dell'atleta non trovata");
      const occurrences = occurrencesForDate(current.gym, schedule, date).filter(
        (occurrence) => occurrence.status !== "conflicted",
      );
      if (occurrences.length === 0) return;
      const now = new Date().toISOString();
      const next = occurrences.reduce(
        (updated, occurrence) => cancelOccurrence(updated, date, occurrence.sourceSlotId, now),
        schedule,
      );
      await sendBroadcast(
        `Il coach ${current.profile.displayName} ti ha rimosso dalla programmazione per la giornata del ${date.split("-").reverse().join("/")}.`,
        next,
      );
    },
    [sendBroadcast],
  );

  const removeMemberFutureBookings = useCallback(
    async (memberId: string) => {
      const current = stateRef.current;
      if (!current.identity || !current.profile || current.profile.role === "member") {
        throw new Error("Solo il coach può rimuovere le prenotazioni degli atleti");
      }
      if (!current.gym) throw new Error("Palestra non disponibile");
      const peer = current.profile.peers.find((candidate) => candidate.id === memberId);
      if (!peer || peer.role !== "member") throw new Error("Atleta non trovato");
      const schedule = current.schedules.find((candidate) => candidate.memberId === memberId);
      if (!schedule) throw new Error("Pianificazione dell'atleta non trovata");
      const today = todayInZone(current.gym.timeZone);
      const hasFutureBooking =
        schedule.recurringSlotIds.length > 0 ||
        schedule.exceptions.some((entry) => entry.kind === "added" && entry.date >= today);
      if (!hasFutureBooking) return;
      await sendBroadcast(
        `Il coach ${current.profile.displayName} ha rimosso tutta la tua programmazione da oggi (${today.split("-").reverse().join("/")}) in poi. Assicurati di reimpostare la programmazione di presenza in palestra continuativa.`,
        cancelFutureBookings(schedule, today),
      );
    },
    [sendBroadcast],
  );

  const markBroadcastRead = useCallback(
    async (broadcastId: string) => {
      const current = stateRef.current;
      if (!current.identity || !current.profile || current.profile.role !== "member") return;
      const existing = current.broadcastReceipts.find(
        (entry) => entry.broadcastId === broadcastId && entry.memberId === current.identity!.id,
      );
      if (existing?.readAt) return;
      const receipt = BroadcastReceiptSchema.parse({
        schemaVersion: 1,
        id: createId("receipt"),
        gymId: current.profile.gymId,
        broadcastId,
        memberId: current.identity.id,
        state: "read",
        occurredAt: new Date().toISOString(),
      });
      const receiptEnvelope = await createAndQueueDomainChange({
        identity: current.identity,
        profile: current.profile,
        kind: "broadcast.receipt",
        payload: receipt,
        recipients: recipientsFor(
          "broadcast.receipt",
          receipt,
          toPublicIdentity(current.identity),
          current.profile.peers,
        ),
        deferQueue: true,
      });
      const projection = await saveBroadcastReceiptOutbox(receipt, {
        envelope: receiptEnvelope,
        attempts: 0,
        nextAttemptAt: new Date().toISOString(),
      });
      await persistBroadcastReceipt(receipt, projection);
      await relayRef.current?.flushQueued();
    },
    [persistBroadcastReceipt],
  );

  const enableNotifications = useCallback(async () => {
    const current = stateRef.current;
    if (
      typeof Notification === "undefined" ||
      !("serviceWorker" in navigator) ||
      !("PushManager" in globalThis)
    ) {
      throw new Error("Le notifiche push non sono supportate su questo dispositivo");
    }
    if (!current.pushPublicKey) {
      throw new Error("Il relay non ha configurato le notifiche push");
    }
    const permission = await Notification.requestPermission();
    setState((previous) => ({ ...previous, notificationPermission: permission }));
    if (permission !== "granted") throw new Error("Permesso notifiche non concesso");
    const registration = await navigator.serviceWorker.ready;
    const subscription =
      (await registration.pushManager.getSubscription()) ??
      (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey(current.pushPublicKey),
      }));
    const parsed = WebPushSubscriptionSchema.parse(subscription.toJSON());
    if (!relayRef.current?.subscribePush(parsed)) {
      throw new Error("Sincronizzazione non attiva: riprova tra poco");
    }
    setState((previous) => ({ ...previous, pushSubscribed: true }));
  }, []);

  const disableNotifications = useCallback(async () => {
    relayRef.current?.unsubscribePush();
    await unsubscribeBrowserPush();
    setState((previous) => ({
      ...previous,
      notificationPermission:
        typeof Notification === "undefined" ? "unsupported" : Notification.permission,
      pushSubscribed: false,
    }));
  }, []);

  const dismissAlert = useCallback(
    async (alertId: string) => {
      if (!state.gym) return;
      const document = await loadCoachProjectionDocument(state.gym.id);
      if (document) {
        await saveCoachProjectionDocument({
          schemaVersion: 1,
          gymId: document.gymId,
          projections: [...document.projections],
          alerts: document.alerts.filter((alert) => alert.id !== alertId),
          generatedAt: document.generatedAt,
        });
      }
      setState((current) => ({
        ...current,
        alerts: current.alerts.filter((alert) => alert.id !== alertId),
      }));
    },
    [state.gym],
  );

  const removeProfile = useCallback(
    async (mnemonic: string, confirmation: string, removeRemoteBackup = false) => {
      if (!state.identity) throw new Error("Profilo locale non trovato");
      if (!state.backupConfirmed) {
        throw new Error("Prima conferma il backup delle 12 parole");
      }
      if (confirmation !== "RIMUOVI") throw new Error("Scrivi RIMUOVI per confermare");
      const restored = identityFromMnemonic(mnemonic);
      if (restored.id !== state.identity.id) {
        throw new Error("Le 12 parole non appartengono al profilo attivo");
      }
      /**
       * Prima la copia remota, poi il dispositivo: cancellare in ordine inverso
       * significherebbe perdere il canale autenticato con cui chiederlo, e
       * lasciare sul relay una copia che l'utente crede rimossa.
       */
      if (removeRemoteBackup) {
        const relay = relayRef.current;
        if (!relay) throw new Error("Sincronizzazione non attiva: la copia sul server resta");
        try {
          await relay.deleteBackup();
        } catch {
          throw new Error(
            "Non è stato possibile cancellare la copia sul server: riprova quando la sincronizzazione è attiva, oppure togli la spunta",
          );
        }
      }
      // Un backup rimandato non deve risalire dopo la cancellazione.
      if (backupTimerRef.current) window.clearTimeout(backupTimerRef.current);
      backupTimerRef.current = undefined;
      backupDirtyRef.current = false;
      // L'impronta vale per il profilo che se ne va: tenerla farebbe passare
      // per «gia' inviato» il primo backup di chi verra' dopo.
      sentBackupRef.current = undefined;
      relayRef.current?.unsubscribePush();
      await unsubscribeBrowserPush().catch(() => undefined);
      relayRef.current?.close();
      relayRef.current = undefined;
      channel?.postMessage("profile-removing");
      await deleteLocalProfileData();
      history.replaceState({}, "", cleanPathKeepingSim());
      setState({
        ready: true,
        backupConfirmed: false,
        schedules: [],
        alerts: [],
        broadcasts: [],
        broadcastManifests: [],
        broadcastReceipts: [],
        notificationPermission:
          typeof Notification === "undefined" ? "unsupported" : Notification.permission,
        pushSubscribed: false,
        invites: [],
        awaitingInitialSync: false,
        sync: emptySync,
      });
      channel?.postMessage("profile-removed");
    },
    [state.backupConfirmed, state.identity],
  );

  /**
   * L'azzeramento del dispositivo. Cancella tutto cio' che sta in locale —
   * identita', chiavi, documenti, cronologia degli snapshot e **la coda delle
   * modifiche non ancora inviate** — e lascia intatta la copia cifrata sul
   * relay.
   *
   * Due cose vanno fatte qui e non altrove.
   *
   * La prima: il `deviceId` viene rigenerato. Il relay registra le conferme di
   * lettura per coppia `(identita', dispositivo)`, quindi un dispositivo che
   * tornasse con lo stesso identificativo non si vedrebbe riconsegnare nulla di
   * cio' che aveva gia' confermato — cioe' proprio la posta che serve a
   * ricostruirsi dopo un azzeramento.
   *
   * La seconda: i timer differiti vanno fermati prima della cancellazione. Un
   * backup rimandato di cinque secondi che scatta dopo il `wipe` ricomporrebbe
   * e rispedirebbe lo stato che si e' appena buttato via.
   */
  const wipeDevice = useCallback(async (confirmation: string) => {
    if (confirmation.trim().toUpperCase() !== "AZZERA") {
      throw new Error("Scrivi AZZERA per confermare");
    }
    if (backupTimerRef.current) window.clearTimeout(backupTimerRef.current);
    backupTimerRef.current = undefined;
    backupDirtyRef.current = false;
    sentBackupRef.current = undefined;
    if (occupancyTimerRef.current) window.clearTimeout(occupancyTimerRef.current);
    occupancyTimerRef.current = undefined;
    alignmentRef.current = 0;
    // Da questo punto le callback del vecchio relay non possono più avviare
    // lavoro. Quello già iniziato termina prima della cancellazione, così non
    // può riaprire IndexedDB e ricreare coda o documenti dopo il wipe.
    profileGenerationRef.current += 1;
    relayRef.current?.unsubscribePush();
    await unsubscribeBrowserPush().catch(() => undefined);
    relayRef.current?.close();
    relayRef.current = undefined;
    await Promise.allSettled([
      alignmentTaskRef.current ?? Promise.resolve(),
      applyQueueRef.current,
    ]);
    alignmentTaskRef.current = undefined;
    // Le altre schede della stessa origine condividono il database: vanno
    // avvisate prima, altrimenti la cancellazione resta bloccata su di loro.
    channel?.postMessage("profile-removing");
    await deleteLocalProfileData();
    history.replaceState({}, "", cleanPathKeepingSim());
    const renewed = await ensureDeviceId();
    deviceIdRef.current = renewed;
    setDeviceId(renewed);
    setState({
      ready: true,
      backupConfirmed: false,
      schedules: [],
      alerts: [],
      broadcasts: [],
      broadcastManifests: [],
      broadcastReceipts: [],
      notificationPermission:
        typeof Notification === "undefined" ? "unsupported" : Notification.permission,
      pushSubscribed: false,
      invites: [],
      awaitingInitialSync: false,
      sync: emptySync,
    });
    channel?.postMessage("profile-removed");
  }, []);

  const clearError = useCallback(
    () =>
      setState((previous) => {
        const next = { ...previous };
        delete next.error;
        return next;
      }),
    [],
  );
  const reconnect = useCallback(() => relayRef.current?.reconnectNow(), []);
  // Tutte le azioni sono callback stabili: questo valore nasce una volta sola.
  const actions = useMemo<StoreActions>(
    () => ({
      createFounder,
      acceptInvite,
      restoreIdentity,
      restoreProfile,
      confirmBackup,
      updateGym,
      updateSchedule,
      book,
      cancel,
      removeMemberBooking,
      removeMemberFutureBookings,
      makeInvite,
      revokeInvite,
      revokePeer,
      dismissAlert,
      sendBroadcast,
      markBroadcastRead,
      enableNotifications,
      disableNotifications,
      removeProfile,
      wipeDevice,
      clearError,
      reconnect,
    }),
    [
      createFounder,
      acceptInvite,
      restoreIdentity,
      restoreProfile,
      confirmBackup,
      updateGym,
      updateSchedule,
      book,
      cancel,
      removeMemberBooking,
      removeMemberFutureBookings,
      makeInvite,
      revokeInvite,
      revokePeer,
      dismissAlert,
      sendBroadcast,
      markBroadcastRead,
      enableNotifications,
      disableNotifications,
      removeProfile,
      wipeDevice,
      clearError,
      reconnect,
    ],
  );
  /**
   * Le dipendenze sono i singoli campi, non `state`: un tick di `sync` lascia
   * questo valore identico e i consumatori di dominio non si accorgono di nulla.
   */
  const domain = useMemo<DomainState>(
    () => ({
      ready: state.ready,
      ...(state.identity ? { identity: state.identity } : {}),
      backupConfirmed: state.backupConfirmed,
      ...(state.profile ? { profile: state.profile } : {}),
      ...(state.gym ? { gym: state.gym } : {}),
      schedules: state.schedules,
      ...(state.occupancy ? { occupancy: state.occupancy } : {}),
      alerts: state.alerts,
      invites: state.invites,
      broadcasts: state.broadcasts,
      broadcastManifests: state.broadcastManifests,
      broadcastReceipts: state.broadcastReceipts,
      ...(state.pushPublicKey ? { pushPublicKey: state.pushPublicKey } : {}),
      notificationPermission: state.notificationPermission,
      pushSubscribed: state.pushSubscribed,
      awaitingInitialSync: state.awaitingInitialSync,
      ...(state.acceptedInviteId ? { acceptedInviteId: state.acceptedInviteId } : {}),
      ...(state.lastStaffContactAt ? { lastStaffContactAt: state.lastStaffContactAt } : {}),
      ...(state.error ? { error: state.error } : {}),
    }),
    [
      state.ready,
      state.identity,
      state.backupConfirmed,
      state.profile,
      state.gym,
      state.schedules,
      state.occupancy,
      state.alerts,
      state.invites,
      state.broadcasts,
      state.broadcastManifests,
      state.broadcastReceipts,
      state.pushPublicKey,
      state.notificationPermission,
      state.pushSubscribed,
      state.awaitingInitialSync,
      state.acceptedInviteId,
      state.lastStaffContactAt,
      state.error,
    ],
  );

  return (
    <ActionsContext.Provider value={actions}>
      <DomainContext.Provider value={domain}>
        <SyncContext.Provider value={state.sync}>{children}</SyncContext.Provider>
      </DomainContext.Provider>
    </ActionsContext.Provider>
  );
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Errore inatteso";
}

export function useAppStore(): StoreValue {
  const domain = useContext(DomainContext);
  const actions = useContext(ActionsContext);
  const value = useMemo<StoreValue | undefined>(
    () => (domain && actions ? { ...domain, ...actions } : undefined),
    [domain, actions],
  );
  if (!value) throw new Error("AppStoreProvider missing");
  return value;
}

/**
 * Il canale per chi deve dire com'e' messa la connessione: badge, avvisi e
 * finestre di errore. Separato da `useAppStore` perche' cambia a ogni battito
 * del relay, e chi mostra dati di dominio non deve pagarne il prezzo.
 */
export function useSyncStatus(): SyncSnapshot {
  const sync = useContext(SyncContext);
  if (!sync) throw new Error("AppStoreProvider missing");
  return sync;
}

export function inviteFromLocation(): SignedInvite | undefined {
  const encoded = new URLSearchParams(location.search).get("invite");
  if (!encoded) return undefined;
  try {
    return decodeInvite(encoded);
  } catch {
    return undefined;
  }
}

export function inviteFromText(value: string): SignedInvite {
  const trimmed = value.trim();
  if (!trimmed) throw new Error("Incolla il link d’invito della palestra");
  try {
    const url = new URL(trimmed, location.origin);
    const encoded = url.searchParams.get("invite");
    return decodeInvite(encoded ?? trimmed);
  } catch {
    throw new Error("Link d’invito non valido");
  }
}
